import React, { useEffect, useState } from "react";
import DashboardLayout from "../../../layouts/DashboardLayout";
import useStyles from "./styles";
import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import CeroDropdown from "../../../components/CeroDropdown";
import { useNavigate } from "react-router-dom";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import CeroButton from "../../../components/CeroButton";
import { useDispatch, useSelector } from "react-redux";
import {
  getStandardsQuestionnaire,
  getStandardsQuestionnaireAnswers,
  getStandardsQuestionnaireHistory,
  listAssessmentCycle,
  listAssessmentCycleCountry,
  listFramework,
  resetSaveStandardsQuestionnaire,
  resetStandardsQuestionnaireAnswers,
  resetSubmitStandardsQuestionnaire,
  saveStandardsQuestionnaire,
  submitStandardsQuestionnaire,
} from "../../../redux/actions";
import CeroInput from "../../../components/CeroInput";
import { useSnackbar } from "notistack";
import { STATUS } from "../../../redux/constants";
import RiskTable from "./RiskTable/RiskTable";
import ResilienceTable from "./RiskTable/ResilienceTable";
import { getCookie } from "../../../services/cookie";
import GriQuestionnaire from "./GRI";
import SasbQuestionnaire from "./SASB";
import BrsrQuestionnaire from "./BRSR";
import CsrdQuestionnaire from "./CSRD";
import IfrsQuestionnaire from "./IFRS";
import dayjs from "dayjs";
import { Details } from "@mui/icons-material";

function StandardsQuestionnaire() {
  const classes = useStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [year, setYear] = useState(null);
  const [country, setCountry] = useState(null);
  const [selectedValues, setSelectedValues] = useState({});
  const [textAnswers, setTextAnswers] = useState({});
  const [dateAnswers, setDateAnswers] = useState({});
  const role = getCookie("role");
  const readOnlyMode = getCookie("read_only_mode") === "disable_edit";

  // Single selector; no conditional hooks:
  const user = useSelector((s) => s?.auth?.user || null);
  const companyName = (user?.company_name || "").trim().toLowerCase();

  const monthNames = [
    "JAN",
    "FEB",
    "MAR",
    "APR",
    "MAY",
    "JUN",
    "JUL",
    "AUG",
    "SEP",
    "OCT",
    "NOV",
    "DEC",
  ];

  const riskTableData = [
    {
      category: "Physical Risks",
      risks: [
        {
          risk: "Increased Frequency Of Extreme Weather Events",
          impacts:
            "Supply Chain Disruptions, Facility Damage, Increased Insurance Costs",
          timePeriod: "",
          applicable: "",
          comments: "",
        },
        {
          risk: "Rising Sea Levels",
          impacts:
            "Property Damage, Need For Relocation, Increased Infrastructure Costs",
          timePeriod: "",
          applicable: "",
          comments: "",
        },
        {
          risk: "Changes In Precipitation Patterns",
          impacts: "Water Scarcity, Operational Delays, Crop Yield Impacts",
          timePeriod: "",
          applicable: "",
          comments: "",
        },
        {
          risk: "Heatwaves Or Temperature Increases",
          impacts:
            "Higher Energy Costs, Reduced Productivity, Equipment Overheating",
          timePeriod: "",
          applicable: "",
          comments: "",
        },
      ],
    },
    {
      category: "Transitional Risks",
      risks: [
        {
          risk: "Regulatory changes (e.g., carbon taxes, ESG requirements)",
          impacts:
            "Increased compliance costs, penalties, adjustments in operations",
          timePeriod: "",
          applicable: "",
          comments: "",
        },
        {
          risk: "Market shifts toward low-carbon products",
          impacts:
            "Declining demand for carbon-intensive products, need for innovation",
          timePeriod: "",
          applicable: "",
          comments: "",
        },
        {
          risk: "Technology advancements",
          impacts:
            "Write-off of current assets and systems, capital investment in newer technology",
          timePeriod: "",
          applicable: "",
          comments: "",
        },
        {
          risk: "Investor/consumer pressure for sustainability",
          impacts:
            "Brand reputation risks, reduced investor interest, loss of market share",
          timePeriod: "",
          applicable: "",
          comments: "",
        },
      ],
    },
  ];

  const resilienceTableData = [
    {
      risks: [
        {
          risk: "2°C or Lower Scenario",
          impacts:
            "Transition to a low-carbon economy, stringent regulations, and widespread adoption of sustainable technologies",
          keyRisk: "",
          keyOpportunity: "",
          resilience: "",
          gapsChallenges: "",
        },
        {
          risk: "4°C Scenario",
          impacts:
            "Limited mitigation efforts, leading to significant physical climate impacts",
          keyRisk: "",
          keyOpportunity: "",
          resilience: "",
          gapsChallenges: "",
        },
        {
          risk: "Baseline Scenario",
          impacts:
            "Current trajectory with moderate regulations and gradual climate action",
          keyRisk: "",
          keyOpportunity: "",
          resilience: "",
          gapsChallenges: "",
        },
        {
          risk: "Business-As-Usual Scenario",
          impacts:
            "No significant policy changes or climate action, continued focus on business-as-usual operations",
          keyRisk: "",
          keyOpportunity: "",
          resilience: "",
          gapsChallenges: "",
        },
        {
          risk: "Physical Impact Scenario",
          impacts:
            "Severe physical climate impacts (e.g., massive flooding, wildfires, etc.)",
          keyRisk: "",
          keyOpportunity: "",
          resilience: "",
          gapsChallenges: "",
        },
        {
          risk: "Technological Disruption Scenario",
          impacts:
            "Sudden breakthrough in clean technologies or AI-driven climate solutions",
          keyRisk: "",
          keyOpportunity: "",
          resilience: "",
          gapsChallenges: "",
        },
      ],
    },
  ];

  const assetValueData = [
    {
      totalNumberOfOperations: "",
      netSales: "",
      totalNumberOfProductOrServicesProvided: "",
      totalAssets: "",
    },
  ];
  const marketServedTableData = [
    {
      marketServed: "",
      graphicBreakDown: "",
      sectorsServed: "",
    },
  ];

  const supplyChainTableData = [
    {
      supplierName: "",
      supplierType: "",
      activitiesType: "",
      businessType: "",
      paymentType: "",
      locationType: "",
    },
  ];

  const downstreamTableData = [
    {
      downstreamName: "",
      downstreamType: "",
      downstreamActivityType: "",
      downstreamBusinessType: "",
      downstreamLocationType: "",
    },
  ];
  const lawRegulationTableData = [
    {
      instance: "",
      reportingPeriod: "",
      instanceType: "",
      lawLocationType: "",
      frequency: "",
      severity: "",
      finePaid: "",
    },
  ];

  const materialTopicsTableData = [
    {
      materialTopic: "",
      materialImpact: "",
      actualPotential: "",
      materialImpactType: "",
      materialTimeFrame: "",
      affectedResources: "",
      materialLocation: "",
    },
  ];

  const strikesTabledata = [
    {
      totalNumberOfOperations: "",
      netSales: "",
      totalNumberOfProductOrServicesProvided: "",
      totalAssets: "",
    },
  ];

  const initiativeTableData = [
    {
      initiativeName: "",
      typeOfSupport: "",
      startEndDate: "",
      status: "",
      priorityCountry: "",
      priorityDisease: "",
      description: "",
    },
  ];

  const drugTableData = [
    {
      inn: "",
      brandName: "",
      formulation: "",
      strength: "",
      therapeuticArea: "",
    },
  ];

  const turnoverTableData = [
    {
      category: "Executives / Senior Managers",
      voluntaryTurnover: "",
      involuntaryTurnover: "",
      classificationStandard: "",
    },
    {
      category: "Mid-Level Managers",
      voluntaryTurnover: "",
      involuntaryTurnover: "",
      classificationStandard: "",
    },
    {
      category: "Professionals (with specialized degrees/certifications)",
      voluntaryTurnover: "",
      involuntaryTurnover: "",
      classificationStandard: "",
    },
    {
      category: "All Others",
      voluntaryTurnover: "",
      involuntaryTurnover: "",
      classificationStandard: "",
    },
  ];

  const corruptionTableData = [
    {
      countryName: "",
      corruptionRiskLevel: "",
      revenue: "",
      businessEthicsRisk: "",
      mitigationMeasures: "",
    },
  ];

  const forestLandTableData = [
    {
      areaType: "Government-Protected Areas",
      areaHectares: "",
      managementStatus: "",
      certifiedStatus: "",
      certificationType: "",
    },
    {
      areaType: "Areas under Conservation Easement",
      areaHectares: "",
      managementStatus: "",
      certifiedStatus: "",
      certificationType: "",
    },
  ];

  const EmployeeCategoryTableData = [
    {
      EmployeeCategory: "Executive Management",
      Women: "",
      Men: "",
      Others: "",
      NotDisclosed: "",
    },
    {
      EmployeeCategory: "Non Executive Management",
      Women: "",
      Men: "",
      Others: "",
      NotDisclosed: "",
    },
    {
      EmployeeCategory: "Professionals",
      Women: "",
      Men: "",
      Others: "",
      NotDisclosed: "",
    },
    {
      EmployeeCategory: "All Other Employees",
      Women: "",
      Men: "",
      Others: "",
      NotDisclosed: "",
    },
  ];

  const EmployeeCategoryTableData1 = [
    {
      EmployeeCategory: "Executive Management",
      Group1: "",
      Group2: "",
      Group3: "",
      NotDisclosed: "",
    },
    {
      EmployeeCategory: "Non Executive Management",
      Group1: "",
      Group2: "",
      Group3: "",
      NotDisclosed: "",
    },
    {
      EmployeeCategory: "Professionals",
      Group1: "",
      Group2: "",
      Group3: "",
      NotDisclosed: "",
    },
    {
      EmployeeCategory: "All Other Employees",
      Group1: "",
      Group2: "",
      Group3: "",
      NotDisclosed: "",
    },
  ];

  const PollutantEmissionData = [
    {
      Pollutant: "Carbon monoxide (CO)",
      Emission: "",
    },
    {
      Pollutant: "Nitrogen oxides (NOx)",
      Emission: "",
    },
    {
      Pollutant: "Sulphur oxides (SOx)",
      Emission: "",
    },
    {
      Pollutant: "Particulate matter (PM10)",
      Emission: "",
    },
    {
      Pollutant: "Manganese oxides (MnO)",
      Emission: "",
    },
    {
      Pollutant: "Lead (Pb)",
      Emission: "",
    },
    {
      Pollutant: "Volatile Organic Compounds (VOCs)",
      Emission: "",
    },
  ];

  const rawSteelProductionData = [
    {
      process: "Raw steel production: basic oxygen furnace processes",
      value: "",
    },
    {
      process: "Raw steel production: electric arc furnace processes",
      value: "",
    },
    {
      process: "Raw steel production: basic oxygen furnace processes (%)",
      value: "",
    },
    {
      process: "Raw steel production: electric arc furnace processes (%)",
      value: "",
    },
  ];

  const workforceTableData = [
    {
      metric: "Total recordable incident rate (TRIR)",
      directEmployees: "",
      contactEmployees: "",
    },
    {
      metric: "Fatality rate",
      directEmployees: "",
      contactEmployees: "",
    },
    {
      metric: "Near miss frequency rate (NMFR)",
      directEmployees: "",
      contactEmployees: "",
    },
  ];

  const workforceTableData1 = [
    {
      metric: "Total recordable incident rate (TRIR)",
      directEmployees: "",
      contactEmployees: "",
    },
    {
      metric: "Fatality rate",
      directEmployees: "",
      contactEmployees: "",
    },
  ];

  const processSafetyData = [
    {
      metric: "Process Safety Incident Count (PSIC)",
      year: "",
    },
    {
      metric: "Process Safety Total Incident Rate (PSTIR)",
      year: "",
    },
    {
      metric: "Process Safety Incident Severity Rate (PSISR)",
      year: "",
    },
  ];

  const defaultEmployeeData = [
    {
      category: "Number of Permanent Employees",
      total: "",
      female: "",
      male: "",
      nonBinary: "",
    },
    {
      category: "Number of Non- Permanent Employees",
      total: "",
      female: "",
      male: "",
      nonBinary: "",
    },
  ];
  const defaultEmployeeData1 = [
    {
      category: "Number of Permanent Employees",
      total: "",
      female: "",
      male: "",
      nonBinary: "",
    },
    {
      category: "Number of Non- Permanent Employees",
      total: "",
      female: "",
      male: "",
      nonBinary: "",
    },
  ];

  const defaultGrievanceData = [
    "Communities",
    "Investors (other than stakeholders)",
    "Shareholders",
    "Employees & Workers",
    "Customers",
    "Value Chain Partners",
    "Other (please specify)",
  ].map((source) => ({
    complaintSource: source,
    grievanceInPlace: false,
    grievancePolicyLink: "",
    currentYearComplaints: "",
    currentYearPending: "",
    currentYearRemarks: "",
    previousYearComplaints: "",
    previousYearPending: "",
    previousYearRemarks: "",
  }));

  const defaultShareData = [
    {
      entityName: "",
      entityType: "",
      sharePercent: "",
    },
  ];

  const defaultMaterialData = [
    {
      materialIssue: "",
      isOpportunity: false,
      reason: "",
      mitigationPlan: "",
      financialImpact: "",
    },
  ];

  const defaultDisclosureData = [
    {
      question:
        "1a. Does Your Policy Cover NGRBC Principles and Core Elements?",
      type: "toggle",
      values: {},
    },
    { question: "1b. Board-Approved Policy?", type: "toggle", values: {} },
    {
      question: "1c. Web Link of the Policies, if available",
      type: "text",
      values: {},
    },
    {
      question: "2. Is Policy Translated into Procedures?",
      type: "toggle",
      values: {},
    },
    {
      question: "3. Do these policies apply to your value chain partners?",
      type: "toggle",
      values: {},
    },
    {
      question: "4. List the National & International codes...",
      type: "text",
      values: {},
      title:
        "List the National & International codes, certifications, labels, or standards (e.g., FSC, Fairtrade, Rainforest Alliance, SA 8000, ISO) adopted by your entity and aligned with each principle.",
    },
    {
      question:
        "5. Specific commitments, goals and targets set by the entity with defined timelines, if any",
      type: "text",
      values: {},
    },
    {
      question:
        "6. Performance of the entity against the specific commitments, goals and targets along with reasons in case the same are not met",
      type: "text",
      values: {},
    },
  ];

  const defaultReviewRows = [
    {
      subject: "1. Performance against above policy and follow up action",
      details: {},
    },
    {
      subject:
        "2. Meeting relevant legal requirements and correcting any violations.",
      details: {},
    },
  ];

  const defaultFrequencyReviewRows = [
    {
      subject: "1. Performance against above policy and follow up action",
      frequencies: {},
    },
    {
      subject:
        "2. Meeting relevant legal requirements and correcting any violations.",
      frequencies: {},
    },
  ];

  const defaultReasonRows = [
    {
      question:
        "The entity does not consider the Principles material to its business",
      type: "toggle",
      values: {},
    },
    {
      question:
        "The entity is not yet in a position to create and implement policies based on the specified principles.",
      type: "toggle",
      values: {},
    },
    {
      question:
        "The entity does not have the financial or/human and technical resources available for the task",
      type: "toggle",
      values: {},
    },
    {
      question: "It is planned to be done in the next financial year",
      type: "toggle",
      values: {},
    },
    {
      question: "Any other reason (please specify)",
      type: "text",
      values: {},
    },
  ];

  const defaultTrainingData = [
    {
      segment: "Board of directors",
      totalPrograms: "",
      topicsCovered: "",
      coveragePercentage: "",
    },
    {
      segment: "Key managerial personnel",
      totalPrograms: "",
      topicsCovered: "",
      coveragePercentage: "",
    },
    {
      segment: "Employees",
      totalPrograms: "",
      topicsCovered: "",
      coveragePercentage: "",
    },
    {
      segment: "Workers",
      totalPrograms: "",
      topicsCovered: "",
      coveragePercentage: "",
    },
  ];

  const defaultLegalActions = [
    {
      type: "Penalty/Fine",
      ngrbc: "",
      agency: "",
      amount: "",
      brief: "",
      appeal: false,
    },
    {
      type: "Settlement",
      ngrbc: "",
      agency: "",
      amount: "",
      brief: "",
      appeal: false,
    },
    {
      type: "Compounding fee",
      ngrbc: "",
      agency: "",
      amount: "",
      brief: "",
      appeal: false,
    },
    {
      type: "Imprisonment",
      ngrbc: "",
      agency: "",
      amount: "",
      brief: "",
      appeal: false,
    },
    {
      type: "Punishment",
      ngrbc: "",
      agency: "",
      amount: "",
      brief: "",
      appeal: false,
    },
  ];

  const defaultCaseStudiesData = [
    {
      casestudies: "",
      nameOfTheRegulatory: "",
    },
  ];

  const defaultRows = [
    { label: "Directors", current: "", previous: "" },
    { label: "KMPs", current: "", previous: "" },
    { label: "Workers", current: "", previous: "" },
    { label: "Employees", current: "", previous: "" },
  ];

  const defaultConflictRows = [
    {
      label:
        "Number of complaints received in relation to issues of Conflict of Interest of the Directors",
      currentNumber: "",
      currentRemarks: "",
      previousNumber: "",
      previousRemarks: "",
    },
    {
      label:
        "Number of complaints received in relation to issues of Conflict of Interest of the KMPs",
      currentNumber: "",
      currentRemarks: "",
      previousNumber: "",
      previousRemarks: "",
    },
  ];

  const defaultAccountRows = {
    label: "Number of days of accounts payable",
    current: "",
    previous: "",
  };

  const defaultConcentrationData = [
    // Concentration of Purchases
    {
      category: "Concentration of Purchases",
      metric: "Purchases from trading houses as % of total purchases",
      current: "",
      previous: "",
    },
    {
      category: "Concentration of Purchases",
      metric: "Number of trading houses where purchases are made from",
      current: "",
      previous: "",
    },
    {
      category: "Concentration of Purchases",
      metric:
        "Purchases from top 10 trading houses as % of total purchases from trading houses",
      current: "",
      previous: "",
    },

    // Concentration of Sales
    {
      category: "Concentration of Sales",
      metric: "Sales to dealers / distributors as % of total sales",
      current: "",
      previous: "",
    },
    {
      category: "Concentration of Sales",
      metric: "Number of dealers / distributors to whom sales are made",
      current: "",
      previous: "",
    },
    {
      category: "Concentration of Sales",
      metric:
        "Sales to top 10 dealers / distributors as % of total sales to dealers / distributors",
      current: "",
      previous: "",
    },

    // Share of Related Party Transactions
    {
      category: "Share of Related Party Transactions (RPTs)",
      metric: "Purchases (Purchases with related parties / Total Purchases)",
      current: "",
      previous: "",
    },
    {
      category: "Share of Related Party Transactions (RPTs)",
      metric: "Sales (Sales to related parties / Total Sales)",
      current: "",
      previous: "",
    },
    {
      category: "Share of Related Party Transactions (RPTs)",
      metric:
        "Loans & advances (Loans & advances given to related parties / Total loans & advances)",
      current: "",
      previous: "",
    },
    {
      category: "Share of Related Party Transactions (RPTs)",
      metric:
        "Investments (Investments in related parties / Total investments made)",
      current: "",
      previous: "",
    },
  ];

  const defaultAwarenessData = [
    { totalPrograms: "", topics: "", coverage: "" },
  ];

  const defaultImprovementRows = [
    {
      label: "R&D",
      currentFY: "",
      previousFY: "",
      details: "",
    },
    {
      label: "Capex",
      currentFY: "",
      previousFY: "",
      details: "",
    },
  ];
  const defaultImprovementRows1 = [
    {
      nicCode: "",
      productName: "",
      turnoverPercentage: "",
      assessmentBoundary: "",
      conductedByExternalAgency: "",
      resultPublicDomain: "",
    },
  ];

  const defaultProductData = [
    {
      product: "",
      Description: "",
      action: "",
    },
  ];

  const defaultrecycledData = [
    {
      indicateinput: "",
      recycledmaterial: "",
    },
  ];

  const defaultWasteData = [
    {
      type: "Plastic waste including packaging",
      current: { recycled: "", reused: "", disposed: "" },
      previous: { recycled: "", reused: "", disposed: "" },
    },
    {
      type: "E-waste",
      current: { recycled: "", reused: "", disposed: "" },
      previous: { recycled: "", reused: "", disposed: "" },
    },
    {
      type: "Hazardous waste",
      current: { recycled: "", reused: "", disposed: "" },
      previous: { recycled: "", reused: "", disposed: "" },
    },
    {
      type: "Other waste",
      current: { recycled: "", reused: "", disposed: "" },
      previous: { recycled: "", reused: "", disposed: "" },
    },
  ];

  const defaultPackageData = [
    {
      indicateproduct: "",
      reclaimedproduct: "",
    },
  ];
  const defaultBenefitData = [
    "Permanent Employees",
    "Other than Permanent Employees",
  ].flatMap((employeeType) =>
    ["Male", "Female", "Total"].map((category) => ({
      employeeType,
      category,
      total: "",
      health: { number: "", percent: "" },
      accident: { number: "", percent: "" },
      maternity: { number: "", percent: "" },
      paternity: { number: "", percent: "" },
      daycare: { number: "", percent: "" },
    }))
  );

  const defaultWorkersBenefitData = [
    "Permanent Workers",
    "Other than Permanent Workers",
  ].flatMap((employeeType) =>
    ["Male", "Female", "Total"].map((category) => ({
      employeeType,
      category,
      total: "",
      health: { number: "", percent: "" },
      accident: { number: "", percent: "" },
      maternity: { number: "", percent: "" },
      paternity: { number: "", percent: "" },
      daycare: { number: "", percent: "" },
    }))
  );

  const defaultWellbeingRows = {
    label:
      "Cost incurred on well-being measures as a % of total revenue of the company",
    currentFY: "",
    previousFY: "",
  };

  const defaultStatutoryTypes = [
    {
      label: "PF",
      current: { employeePercent: "", workerPercent: "", deposited: "" },
      previous: { employeePercent: "", workerPercent: "", deposited: "" },
    },
    {
      label: "Gratuity",
      current: { employeePercent: "", workerPercent: "", deposited: "" },
      previous: { employeePercent: "", workerPercent: "", deposited: "" },
    },
    {
      label: "ESI",
      current: { employeePercent: "", workerPercent: "", deposited: "" },
      previous: { employeePercent: "", workerPercent: "", deposited: "" },
    },
    {
      label: "Others - Please Specify",
      current: { employeePercent: "", workerPercent: "", deposited: "" },
      previous: { employeePercent: "", workerPercent: "", deposited: "" },
    },
  ];

  const defaultgenders = [
    {
      category: "Male",
      EmpReturn: "",
      EmpRetention: "",
      workerReturn: "",
      workerRetention: "",
    },
    {
      category: "Female",
      EmpReturn: "",
      EmpRetention: "",
      workerReturn: "",
      workerRetention: "",
    },
    {
      category: "Total",
      EmpReturn: "",
      EmpRetention: "",
      workerReturn: "",
      workerRetention: "",
    },
  ];

  const defaultcategories = [
    { category: "Permanent Workers", value: "" },
    { category: "Other Than Permanent Workers", value: "" },
    { category: "Permanent Employees", value: "" },
    { category: "Other Than Permanent Employees", value: "" },
  ];

  const defaultUnionData = [
    "Total Permanent Employees",
    "Total Permanent Workers",
  ].flatMap((group) =>
    ["Male", "Female"].map((gender) => ({
      group,
      gender,
      current: { total: "", union: "", percent: "" },
      previous: { total: "", union: "", percent: "" },
    }))
  );

  const defaultTrainingmeasuresData = ["Employees", "Workers"].flatMap(
    (group) =>
      ["Male", "Female", "Total"].map((gender) => ({
        group,
        gender,
        current: {
          total: "",
          health: "",
          healthPercent: "",
          skill: "",
          skillPercent: "",
        },
        previous: {
          total: "",
          health: "",
          healthPercent: "",
          skill: "",
          skillPercent: "",
        },
      }))
  );

  const defaultFYData = ["Employees", "Workers"].flatMap((group) =>
    ["Male", "Female", "Total"].map((gender) => ({
      group,
      gender,
      current: {
        total: "",
        number: "",
        percentage: "",
      },
      previous: {
        total: "",
        number: "",
        percentage: "",
      },
    }))
  );

  const incidentTypes = [
    {
      incident: "Lost time injury frequecy Rate(LTIFR)",
      category: "Employees",
      current: 0,
      previous: 0,
    },
    {
      incident: "Lost time injury frequecy Rate(LTIFR)",
      category: "Workers",
      current: 0,
      previous: 0,
    },
    {
      incident: "Total recordable work-related",
      category: "Employees",
      current: 0,
      previous: 0,
    },
    {
      incident: "Total recordable work-related",
      category: "Workers",
      current: 0,
      previous: 0,
    },
    {
      incident: "No. of fatalities",
      category: "Employees",
      current: 0,
      previous: 0,
    },
    {
      incident: "No. of fatalities",
      category: "Workers",
      current: 0,
      previous: 0,
    },
    {
      incident: "High consequence work-related injury or ill-health",
      category: "Employees",
      current: 0,
      previous: 0,
    },
    {
      incident: "High consequence work-related injury or ill-health",
      category: "Workers",
      current: 0,
      previous: 0,
    },
  ];

  const defaultPFData = [
    {
      category: "Working Conditions",
      current_filed: "",
      current_pending: "",
      current_remarks: "",
      previous_filed: "",
      previous_pending: "",
      previous_remarks: "",
    },
    {
      category: "Health & Safety",
      current_filed: "",
      current_pending: "",
      current_remarks: "",
      previous_filed: "",
      previous_pending: "",
      previous_remarks: "",
    },
  ];

  const defaultAssessmentData = [
    {
      category: "Health & Safety practices",
      details: "",
    },
    {
      category: "Working conditions",
      details: "",
    },
  ];
  const defaultRehabData = [
    {
      group: "Employees",
      affected_current: "",
      rehired_current: "",
      affected_previous: "",
      rehired_previous: "",
    },
    {
      group: "Workers",
      affected_current: "",
      rehired_current: "",
      affected_previous: "",
      rehired_previous: "",
    },
  ];

  const defaultValuechainData = [
    { group: "Health & Safety practices", details: "" },
    { group: "Working conditions", details: "" },
  ];
  const defaultStakeholderData = [
    {
      stakeholder: "",
      identified: "",
      chanels: "",
      frequency: "",
      purpose: "",
    },
  ];

  const defaultCoverageData = [
    {
      group: "Employees",
      category: "Permanent",
      current: { total: "", covered: "", percentage: "" },
      previous: { total: "", covered: "", percentage: "" },
    },
    {
      group: "Employees",
      category: "Other Than Permanent",
      current: { total: "", covered: "", percentage: "" },
      previous: { total: "", covered: "", percentage: "" },
    },
    {
      group: "Employees",
      category: "Total Employees",
      current: { total: "", covered: "", percentage: "" },
      previous: { total: "", covered: "", percentage: "" },
    },
    {
      group: "Workers",
      category: "Permanent",
      current: { total: "", covered: "", percentage: "" },
      previous: { total: "", covered: "", percentage: "" },
    },
    {
      group: "Workers",
      category: "Other Than Permanent",
      current: { total: "", covered: "", percentage: "" },
      previous: { total: "", covered: "", percentage: "" },
    },
    {
      group: "Workers",
      category: "Total Workers",
      current: { total: "", covered: "", percentage: "" },
      previous: { total: "", covered: "", percentage: "" },
    },
  ];

  const defaultsections = [
    {
      group: "Employees",
      type: "Permanent",
      current_total: "",
      current_equal_min: "",
      current_percent_equal: "",
      current_more_min: "",
      current_percent_more: "",
      prev_total: "",
      prev_equal_min: "",
      prev_percent_equal: "",
      prev_more_min: "",
      prev_percent_more: "",
    },
    {
      group: "Employees",
      type: "Male",
      current_total: "",
      current_equal_min: "",
      current_percent_equal: "",
      current_more_min: "",
      current_percent_more: "",
      prev_total: "",
      prev_equal_min: "",
      prev_percent_equal: "",
      prev_more_min: "",
      prev_percent_more: "",
    },
    {
      group: "Employees",
      type: "Female",
      current_total: "",
      current_equal_min: "",
      current_percent_equal: "",
      current_more_min: "",
      current_percent_more: "",
      prev_total: "",
      prev_equal_min: "",
      prev_percent_equal: "",
      prev_more_min: "",
      prev_percent_more: "",
    },
    {
      group: "Employees",
      type: "Other than Permanent",
      current_total: "",
      current_equal_min: "",
      current_percent_equal: "",
      current_more_min: "",
      current_percent_more: "",
      prev_total: "",
      prev_equal_min: "",
      prev_percent_equal: "",
      prev_more_min: "",
      prev_percent_more: "",
    },
    {
      group: "Workers",
      type: "Permanent",
      current_total: "",
      current_equal_min: "",
      current_percent_equal: "",
      current_more_min: "",
      current_percent_more: "",
      prev_total: "",
      prev_equal_min: "",
      prev_percent_equal: "",
      prev_more_min: "",
      prev_percent_more: "",
    },
    {
      group: "Workers",
      type: "Male",
      current_total: "",
      current_equal_min: "",
      current_percent_equal: "",
      current_more_min: "",
      current_percent_more: "",
      prev_total: "",
      prev_equal_min: "",
      prev_percent_equal: "",
      prev_more_min: "",
      prev_percent_more: "",
    },
    {
      group: "Workers",
      type: "Female",
      current_total: "",
      current_equal_min: "",
      current_percent_equal: "",
      current_more_min: "",
      current_percent_more: "",
      prev_total: "",
      prev_equal_min: "",
      prev_percent_equal: "",
      prev_more_min: "",
      prev_percent_more: "",
    },
    {
      group: "Workers",
      type: "Other than Permanent",
      current_total: "",
      current_equal_min: "",
      current_percent_equal: "",
      current_more_min: "",
      current_percent_more: "",
      prev_total: "",
      prev_equal_min: "",
      prev_percent_equal: "",
      prev_more_min: "",
      prev_percent_more: "",
    },
  ];

  const defaultRoles = [
    {
      category: "Board of directors (BOD)",
      male_number: "",
      male_median: "",
      female_number: "",
      female_median: "",
    },
    {
      category: "Key Managerial persons (KMP)",
      male_number: "",
      male_median: "",
      female_number: "",
      female_median: "",
    },
    {
      category: "Employees",
      male_number: "",
      male_median: "",
      female_number: "",
      female_median: "",
    },
    {
      category: "Workers",
      male_number: "",
      male_median: "",
      female_number: "",
      female_median: "",
    },
  ];

  const defaultFemaleWage = {
    Category: "Gross wages paid to females as % of total wages",
    currentFY: "",
    previousFY: "",
  };
  const defaultrightscategories = [
    {
      category: "Sexual Harassment",
      current_filed: "",
      current_pending: "",
      current_remarks: "",
      previous_filed: "",
      previous_pending: "",
      previous_remarks: "",
    },
    {
      category: "Discrimination at work place",
      current_filed: "",
      current_pending: "",
      current_remarks: "",
      previous_filed: "",
      previous_pending: "",
      previous_remarks: "",
    },
    {
      category: "Child Labor",
      current_filed: "",
      current_pending: "",
      current_remarks: "",
      previous_filed: "",
      previous_pending: "",
      previous_remarks: "",
    },
    {
      category: "Forced Labor/ Involuntary Labor",
      current_filed: "",
      current_pending: "",
      current_remarks: "",
      previous_filed: "",
      previous_pending: "",
      previous_remarks: "",
    },
    {
      category: "Wages",
      current_filed: "",
      current_pending: "",
      current_remarks: "",
      previous_filed: "",
      previous_pending: "",
      previous_remarks: "",
    },
    {
      category: "Other human rights related issues",
      current_filed: "",
      current_pending: "",
      current_remarks: "",
      previous_filed: "",
      previous_pending: "",
      previous_remarks: "",
    },
  ];

  const poshComplaintRows = [
    {
      label:
        "Total complaints reported under sexual harassment on woman at workplace (Prevention, Prohibition, Redressal) Act,2013 (POSH)",
      key: "totalComplaints",
      current: "",
      previous: "",
    },
    {
      label: "Complaints on POSH as a % of female employees/ workers",
      key: "percentComplaints",
      current: "",
      previous: "",
    },
    {
      label: "Complaints on POSH upheld",
      key: "complaintsUpheld",
      current: "",
      previous: "",
    },
  ];

  const defaultAssessmentIssues = [
    { label: "Child labour", value: "" },
    { label: "Forced/Involuntary labour", value: "" },
    { label: "Sexual harassment", value: "" },
    { label: "Discrimination at workplace", value: "" },
    { label: "Wages", value: "" },
    { label: "Others", value: "" },
  ];

  const defaultValueChainIssues = [
    { label: "Child labour", value: "" },
    { label: "Forced/Involuntary labour", value: "" },
    { label: "Sexual harassment", value: "" },
    { label: "Discrimination at workplace", value: "" },
    { label: "Wages", value: "" },
    { label: "Others", value: "" },
  ];

  const defaultEnergyData = [
    {
      section: "From Renewable sources",
      label: "Total electricity consumption (A)",
      key: "renewableA",
      current: "",
      previous: "",
    },
    {
      section: "From Renewable sources",
      label: "Total fuel consumption (B)",
      key: "renewableB",
      current: "",
      previous: "",
    },
    {
      section: "From Renewable sources",
      label: "Energy consumption through other sources (C)",
      key: "renewableC",
      current: "",
      previous: "",
    },
    {
      section: "From Renewable sources",
      label: "Total energy consumed from renewable sources (A+B+C)",
      key: "renewableTotal",
      current: "",
      previous: "",
    },
    {
      section: "From Non-renewable sources",
      label: "Total electricity consumption (D)",
      key: "nonRenewableD",
      current: "",
      previous: "",
    },
    {
      section: "From Non-renewable sources",
      label: "Total fuel consumption (E)",
      key: "nonRenewableE",
      current: "",
      previous: "",
    },
    {
      section: "From Non-renewable sources",
      label: "Energy consumption through other sources (F)",
      key: "nonRenewableF",
      current: "",
      previous: "",
    },
    {
      section: "From Non-renewable sources",
      label: "Total energy consumed from non-renewable sources (D+E+F)",
      key: "nonRenewableTotal",
      current: "",
      previous: "",
    },
    {
      section: "Total Energy consumed (A+B+C+D+E+F)",
      label:
        "Energy intensity per rupee of turnover (Total energy consumed/ Revenue from operations)",
      key: "intensityTurnover",
      current: "",
      previous: "",
    },
    {
      section: "Total Energy consumed (A+B+C+D+E+F)",
      label:
        "Energy intensity per rupee of turnover adjusted for Purchasing Power Parity (PPP)",
      key: "intensityPPP",
      current: "",
      previous: "",
    },
    {
      section: "Total Energy consumed (A+B+C+D+E+F)",
      label: "Energy intensity in terms of physical output",
      key: "intensityOutput",
      current: "",
      previous: "",
    },
    {
      section: "Total Energy consumed (A+B+C+D+E+F)",
      label:
        "Energy intensity (optional) – the relevant metric may be selected by the entity",
      key: "intensityOptional",
      current: "",
      previous: "",
    },
  ];
  const defaultWaterData = [
    {
      section: "Water withdrawal by source (in kiloliters)",
      parameter: "(i) Surface water",
      key: "surface",
      current: "",
      previous: "",
    },
    {
      section: "Water withdrawal by source (in kiloliters)",
      parameter: "(ii) Ground water",
      key: "ground",
      current: "",
      previous: "",
    },
    {
      section: "Water withdrawal by source (in kiloliters)",
      parameter: "(iii) Thirdparty water",
      key: "thirdparty",
      current: "",
      previous: "",
    },
    {
      section: "Water withdrawal by source (in kiloliters)",
      parameter: "(iv) Seawater / desalinated water",
      key: "seawater",
      current: "",
      previous: "",
    },
    {
      section: "Water withdrawal by source (in kiloliters)",
      parameter: "(v) Others",
      key: "others",
      current: "",
      previous: "",
    },
    {
      section: "Water withdrawal by source (in kiloliters)",
      parameter:
        "Total volume of water withdrawal (in kilolitres) (i + ii + iii + iv + v)",
      key: "totalWithdrawal",
      current: "",
      previous: "",
    },
    {
      section: "Water withdrawal by source (in kiloliters)",
      parameter: "Total volume of water consumption (in kilolitres)",
      key: "totalConsumption",
      current: "",
      previous: "",
    },
    {
      section: "Water withdrawal by source (in kiloliters)",
      parameter:
        "Water intensity per rupee of turnover (Total water consumption / Revenue from operations)",
      key: "intensityTurnover",
      current: "",
      previous: "",
    },
    {
      section: "Water withdrawal by source (in kiloliters)",
      parameter:
        "Water intensity per rupee of turnover adjusted for Purchasing Power Parity (PPP) (Total water consumption / Revenue from operations adjusted for PPP)",
      key: "intensityPPP",
      current: "",
      previous: "",
    },
    {
      section: "Water withdrawal by source (in kiloliters)",
      parameter: "Water intensity in terms of physical output",
      key: "intensityOutput",
      current: "",
      previous: "",
    },
    {
      section: "Water withdrawal by source (in kiloliters)",
      parameter:
        "Water intensity (optional) – the relevant metric may be selected by the entity",
      key: "intensityOptional",
      current: "",
      previous: "",
    },
  ];

  const dischargeCategories = [
    {
      section: "(i) To Surface water",
      subcategory: "No treatment",
      key: "surface",
      subkey: "noTreatment",
      label: "• No treatment",
      current: "",
      previous: "",
    },
    {
      section: "(i) To Surface water",
      subcategory: "With treatment",
      key: "surface",
      subkey: "withTreatment",
      label: "• With treatment – please specify level of treatment",
      current: "",
      previous: "",
    },

    {
      section: "(ii) To Groundwater",
      subcategory: "No treatment",
      key: "groundwater",
      subkey: "noTreatment",
      label: "• No treatment",
      current: "",
      previous: "",
    },
    {
      section: "(ii) To Groundwater",
      subcategory: "With treatment",
      key: "groundwater",
      subkey: "withTreatment",
      label: "• With treatment – please specify level of treatment",
      current: "",
      previous: "",
    },

    {
      section: "(iii) To Seawater",
      subcategory: "No treatment",
      key: "seawater",
      subkey: "noTreatment",
      label: "• No treatment",
      current: "",
      previous: "",
    },
    {
      section: "(iii) To Seawater",
      subcategory: "With treatment",
      key: "seawater",
      subkey: "withTreatment",
      label: "• With treatment – please specify level of treatment",
      current: "",
      previous: "",
    },

    {
      section: "(iv) Sent to third-parties",
      subcategory: "No treatment",
      key: "thirdparty",
      subkey: "noTreatment",
      label: "• No treatment",
      current: "",
      previous: "",
    },
    {
      section: "(iv) Sent to third-parties",
      subcategory: "With treatment",
      key: "thirdparty",
      subkey: "withTreatment",
      label: "• With treatment – please specify level of treatment",
      current: "",
      previous: "",
    },

    {
      section: "(v) Others",
      subcategory: "No treatment",
      key: "others",
      subkey: "noTreatment",
      label: "• No treatment",
      current: "",
      previous: "",
    },
    {
      section: "(v) Others",
      subcategory: "With treatment",
      key: "others",
      subkey: "withTreatment",
      label: "• With treatment – please specify level of treatment",
      current: "",
      previous: "",
    },

    {
      section: "",
      subcategory: "",
      key: "totalDischarge",
      subkey: "",
      label: "Total water discharged (in kilolitres)",
      current: "",
      previous: "",
    },
  ];

  const pollutantRows = [
    "NOx",
    "SOx",
    "Particulate matter (PM)",
    "Persistent organic pollutants (POP)",
    "Volatile organic compounds (VOC)",
    "Hazardous air pollutants (HAP)",
    "Others – please specify",
  ];
  const defaultAirPollutantData = pollutantRows.map((param) => ({
    parameter: param,
    unit: "",
    current: "",
    previous: "",
  }));

  const defaultscopeparameters = [
    {
      label:
        "Total Scope 1 emissions (Break-up of the GHG into CO2, CH4, N2O, HFCs, PFCs, SF6, NF3, if available)",
      unit: "Metric tonnes of CO2 equivalent",
      current: "",
      previous: "",
    },
    {
      label:
        "Total Scope 2 emissions (Break-up of the GHG into CO2, CH4, N2O, HFCs, PFCs, SF6, NF3, if available)",
      unit: "Metric tonnes of CO2 equivalent",
      current: "",
      previous: "",
    },
    {
      label:
        "Total Scope 1 and Scope 2 emission intensity per rupee of turnover (Total Scope 1 and Scope 2 GHG emissions/ Revenue from operations)",
      unit: "",
      current: "",
      previous: "",
    },
    {
      label:
        "Total Scope 1 and Scope 2 emission intensity per rupee of turnover adjusted for Purchasing Power Parity (PPP) (Total Scope 1 and Scope 2 GHG emissions / Revenue from operations adjusted for PPP)",
      unit: "",
      current: "",
      previous: "",
    },
    {
      label:
        "Total Scope 1 and Scope 2 emission intensity in terms of physical output",
      unit: "",
      current: "",
      previous: "",
    },
    {
      label:
        "Total Scope 1 and Scope 2 emission intensity (optional) – the relevant metric may be selected by the entity",
      unit: "",
      current: "",
      previous: "",
    },
  ];

  const wasteParameters = [
    {
      section: "Total Waste generated (in metric tonnes)",
      label: "Plastic waste (A)",
      current: "",
      previous: "",
    },
    {
      section: "Total Waste generated (in metric tonnes)",
      label: "E-waste (B)",
      current: "",
      previous: "",
    },
    {
      section: "Total Waste generated (in metric tonnes)",
      label: "Bio-medical waste (C)",
      current: "",
      previous: "",
    },
    {
      section: "Total Waste generated (in metric tonnes)",
      label: "Construction and demolition waste (D)",
      current: "",
      previous: "",
    },
    {
      section: "Total Waste generated (in metric tonnes)",
      label: "Battery waste (E)",
      current: "",
      previous: "",
    },
    {
      section: "Total Waste generated (in metric tonnes)",
      label: "Radioactive waste (F)",
      current: "",
      previous: "",
    },
    {
      section: "Total Waste generated (in metric tonnes)",
      label: "Other Hazardous waste. Please Specify, if any. (G)",
      current: "",
      previous: "",
    },
    {
      section: "Total Waste generated (in metric tonnes)",
      label: "Other Non-hazardous waste generated (H). Please specify, if any.",
      current: "",
      previous: "",
    },
    {
      section: "Total Waste generated (in metric tonnes)",
      label: "Total (A+B + C + D + E + F + G + H)",
      isTotal: true,
      current: "",
      previous: "",
    },
    {
      section: "Total Waste generated (in metric tonnes)",
      label:
        "Waste intensity per rupee of turnover (Total waste generated / Revenue from operations)",
      current: "",
      previous: "",
    },
    {
      section: "Total Waste generated (in metric tonnes)",
      label:
        "Waste intensity per rupee of turnover adjusted for Purchasing Power Parity (PPP)",
      current: "",
      previous: "",
    },
    {
      section: "Total Waste generated (in metric tonnes)",
      label: "Waste intensity in terms of physical output",
      current: "",
      previous: "",
    },
    {
      section: "Total Waste generated (in metric tonnes)",
      label:
        "Waste intensity (optional) – the relevant metric may be selected by the entity",
      current: "",
      previous: "",
    },

    // Recovered section
    {
      section:
        "For each category of waste generated, total waste recovered through recycling, re-using or other recovery",
      label: "(i) Recycled",
      current: "",
      previous: "",
    },
    {
      section:
        "For each category of waste generated, total waste recovered through recycling, re-using or other recovery",
      label: "(ii) Re-used",
      current: "",
      previous: "",
    },
    {
      section:
        "For each category of waste generated, total waste recovered through recycling, re-using or other recovery",
      label: "(iii) Other recovery operations",
      current: "",
      previous: "",
    },
    {
      section:
        "For each category of waste generated, total waste recovered through recycling, re-using or other recovery",
      label: "Total",
      isTotal: true,
      current: "",
      previous: "",
    },

    // Disposed section
    {
      section:
        "For each category of waste generated, total waste disposed by nature of disposal method",
      label: "(i) Incineration",
      current: "",
      previous: "",
    },
    {
      section:
        "For each category of waste generated, total waste disposed by nature of disposal method",
      label: "(ii) Landfilling",
      current: "",
      previous: "",
    },
    {
      section:
        "For each category of waste generated, total waste disposed by nature of disposal method",
      label: "(iii) Other disposal operations",
      current: "",
      previous: "",
    },
    {
      section:
        "For each category of waste generated, total waste disposed by nature of disposal method",
      label: "Total",
      isTotal: true,
      current: "",
      previous: "",
    },
  ];

  const defaultenvclearanceData = [
    {
      location: "",
      operation: "",
      compliance: "",
    },
  ];
  const defaultEIAData = [
    {
      projectName: "",
      notificationNo: "",
      date: "",
      conductedExternally: "",
      resultsPublic: "",
      webLink: "",
    },
  ];

  const defaultregulatoryData = [
    {
      lawViolated: "",
      nonComplianceDetails: "",
      penaltiesTaken: "",
      correctiveAction: "",
    },
  ];

  const defaultusageWaterData = [
    // Withdrawal Section
    {
      section: "Water withdrawal by source (in kilolitres)",
      label: "(i) Surface water",
      current: "",
      previous: "",
    },
    {
      section: "Water withdrawal by source (in kilolitres)",
      label: "(ii) Ground water",
      current: "",
      previous: "",
    },
    {
      section: "Water withdrawal by source (in kilolitres)",
      label: "(iii) Third party water",
      current: "",
      previous: "",
    },
    {
      section: "Water withdrawal by source (in kilolitres)",
      label: "(iv) Seawater / desalinated water",
      current: "",
      previous: "",
    },
    {
      section: "Water withdrawal by source (in kilolitres)",
      label: "(v) Others",
      current: "",
      previous: "",
    },
    {
      section: "Water withdrawal by source (in kilolitres)",
      label: "Total volume of water withdrawal (i + ii + iii + iv + v)",
      current: "",
      previous: "",
    },
    {
      section: "Water withdrawal by source (in kilolitres)",
      label: "Total volume of water consumption (in kilolitres)",
      current: "",
      previous: "",
    },
    {
      section: "Water withdrawal by source (in kilolitres)",
      label: "Water intensity per rupee of turnover (Water consumed/turnover)",
      current: "",
      previous: "",
    },
    {
      section: "Water withdrawal by source (in kilolitres)",
      label:
        "Water intensity (optional) the relevant metric may be selected by the entity",
      current: "",
      previous: "",
    },

    // Discharge Section
    {
      section: "(i) Into Surface water",
      label: "• No treatment",
      current: "",
      previous: "",
    },
    {
      section: "(i) Into Surface water",
      label: "• With treatment – please specify level of treatment",
      current: "",
      previous: "",
    },
    {
      section: "(ii) Into Groundwater",
      label: "• No treatment",
      current: "",
      previous: "",
    },
    {
      section: "(ii) Into Groundwater",
      label: "• With treatment – please specify level of treatment",
      current: "",
      previous: "",
    },
    {
      section: "(iii) Into Seawater",
      label: "• No treatment",
      current: "",
      previous: "",
    },
    {
      section: "(iii) Into Seawater",
      label: "• With treatment – please specify level of treatment",
      current: "",
      previous: "",
    },
    {
      section: "(iv) Sent to third-parties",
      label: "• No treatment",
      current: "",
      previous: "",
    },
    {
      section: "(iv) Sent to third-parties",
      label: "• With treatment – please specify level of treatment",
      current: "",
      previous: "",
    },
    {
      section: "(v) Others",
      label: "• No treatment",
      current: "",
      previous: "",
    },
    {
      section: "(v) Others",
      label: "• With treatment – please specify level of treatment",
      current: "",
      previous: "",
    },
    {
      section: "(v) Others",
      label: "Total water discharged (in kilolitres)",
      current: "",
      previous: "",
    },
  ];

  const defaultScope3Data = [
    {
      parameter: "Total Scope 3 emissions",
      unit: "Metric tonnes of CO2 equivalent",
      current: "",
      previous: "",
    },
    {
      parameter: "Total Scope 3 emissions per rupee of turnover",
      unit: "",
      current: "",
      previous: "",
    },
    {
      parameter: "Total Scope 3 emission intensity",
      unit: "",
      current: "",
      previous: "",
    },
  ];

  const defaultInitiativeData = [
    {
      initiative: "",
      details: "",
      outcome: "",
    },
  ];

  const defaultTradeData = [
    { name: "", reach: "" },
    { name: "", reach: "" },
    { name: "", reach: "" },
    { name: "", reach: "" },
    { name: "", reach: "" },
    { name: "", reach: "" },
    { name: "", reach: "" },
    { name: "", reach: "" },
    { name: "", reach: "" },
    { name: "", reach: "" },
  ];

  const defaultAuthorityData = [{ authority: "", caseBrief: "", reach: "" }];
  const defaultAdvocacyData = [
    { policy: "", method: "", publicInfo: "", frequency: "" },
  ];

  const defaultSiaData = [
    {
      project: "",
      notificationNo: "",
      date: "",
      externalAgency: "",
      publicDomain: "",
      webLink: "",
    },
  ];

  const defaultResettlementData = [
    {
      projectName: "",
      state: "",
      district: "",
      pafCount: "",
      pafCoveredPercent: "",
      amountPaid: "",
    },
  ];
  const defaultSourcingData = [
    {
      parameter: "Directly sourced from MSMEs/small producers",
      current: "",
      previous: "",
    },
    { parameter: "Directly from within India", current: "", previous: "" },
  ];

  const defaultLocationData = [
    { location: "Rural", current: "", previous: "" },
    { location: "Semi-Urban", current: "", previous: "" },
    { location: "Urban", current: "", previous: "" },
    { location: "Metropolitan", current: "", previous: "" },
  ];

  const defaultSocialImpactData = [{ impact: "", action: "" }];
  const defaultAspirationalDistrictData = [
    { state: "", district: "", amount: "" },
  ];
  const defaultTraditionalKnowledgeData = [
    {
      knowledge: "",
      owned: "",
      benefitShared: "",
      basis: "",
    },
  ];
  const defaultRegulatoryCaseData = [
    { authority: "", caseBrief: "", actionTaken: "" },
  ];

  const defaultCsrBeneficiaryData = [
    { project: "", beneficiaries: "", vulnerablePercentage: "" },
  ];

  const defaultTurnoverData = [
    {
      parameter: "Environmental and social parameters relevant to the product",
      percentage: "",
    },
    { parameter: "Safe and responsible usage", percentage: "" },

    { parameter: "Recycling and/or safe disposal", percentage: "" },
  ];

  const defaultCustomerComplaintData = [
    {
      topic: "Data privacy",
      currentReceived: "",
      currentPending: "",
      previousReceived: "",
      previousPending: "",
      currentRemarks: "",
      previousRemarks: "",
    },
    {
      topic: "Advertising",
      currentReceived: "",
      currentPending: "",
      previousReceived: "",
      previousPending: "",
      currentRemarks: "",
      previousRemarks: "",
    },
    {
      topic: "Cyber-security",
      currentReceived: "",
      currentPending: "",
      previousReceived: "",
      previousPending: "",
      currentRemarks: "",
      previousRemarks: "",
    },
    {
      topic: "Delivery of essential services",
      currentReceived: "",
      currentPending: "",
      previousReceived: "",
      previousPending: "",
      currentRemarks: "",
      previousRemarks: "",
    },

    {
      topic: "Restrictive Trade Practices",
      currentReceived: "",
      currentPending: "",
      previousReceived: "",
      previousPending: "",
      currentRemarks: "",
      previousRemarks: "",
    },
    {
      topic: "Unfair Trade Practices",
      currentReceived: "",
      currentPending: "",
      previousReceived: "",
      previousPending: "",
      currentRemarks: "",
      previousRemarks: "",
    },
    {
      topic: "Other",
      currentReceived: "",
      currentPending: "",
      previousReceived: "",
      previousPending: "",
      currentRemarks: "",
      previousRemarks: "",
    },
  ];
  const defaultRecallData = [
    { type: "Voluntary recalls", number: "", reason: "" },
    { type: "Forced recalls", number: "", reason: "" },
  ];

  const defaultOversightFormData = [
    {
      aspect: "Strategy and business model",
      oversightForm: "",
    },
    {
      aspect: "Risk and opportunity assessment",
      oversightForm: "",
    },
    {
      aspect: "Policies, targets, and action plans",
      oversightForm: "",
    },
    {
      aspect: "Reporting and disclosures",
      oversightForm: "",
    },
  ];
  const defaultDueDiligenceData = [
    {
      coreElement:
        "Embedding due diligence in governance, strategy and business model",
      relatedDisclosure: "",
      coversPeople: "",
      coversEnvironment: "",
    },
    {
      coreElement:
        "Engaging with affected stakeholders in all key steps of the due diligence",
      relatedDisclosure: "",
      coversPeople: "",
      coversEnvironment: "",
    },
    {
      coreElement: "Identifying and assessing adverse impacts",
      relatedDisclosure: "",
      coversPeople: "",
      coversEnvironment: "",
    },
    {
      coreElement: "Taking actions to address those adverse impacts",
      relatedDisclosure: "",
      coversPeople: "",
      coversEnvironment: "",
    },
    {
      coreElement:
        "Tracking the effectiveness of these efforts and communicating",
      relatedDisclosure: "",
      coversPeople: "",
      coversEnvironment: "",
    },
  ];

  const defaultPolicyTableData = [
    {
      policy: "",
      description: "",
      scope: "",
      accountablePerson: "",
      recognisedInstruments: "",
      availability: "",
    },
    {
      policy: "",
      description: "",
      scope: "",
      accountablePerson: "",
      recognisedInstruments: "",
      availability: "",
    },
    {
      policy: "",
      description: "",
      scope: "",
      accountablePerson: "",
      recognisedInstruments: "",
      availability: "",
    },
  ];

  const defaultSustainabilityTargetsData = [
    {
      sustainabilityMatter: "",
      targetDescription: "",
      targetType: "",
      targetYear: "",
      baselineYear: "",
    },
    {
      sustainabilityMatter: "",
      targetDescription: "",
      targetType: "",
      targetYear: "",
      baselineYear: "",
    },
  ];

  const defaultResourceAllocationData = [
    {
      sustainabilityMatter: "",
      capex: "",
      opex: "",
      allocationYear: "",
      futureResources: "",
    },
    {
      sustainabilityMatter: "",
      capex: "",
      opex: "",
      allocationYear: "",
      futureResources: "",
    },
  ];

  const defaultGenderRepresentationData = {
    executiveManagement: { male: "", female: "", other: "", notDisclosed: "" },
    nonExecutiveManagement: {
      male: "",
      female: "",
      other: "",
      notDisclosed: "",
    },
    professionals: { male: "", female: "", other: "", notDisclosed: "" },
    allOtherEmployees: { male: "", female: "", other: "", notDisclosed: "" },
  };

  const defaultGroupRepresentationData = {
    executiveManagement: { groupA: "", groupB: "", groupC: "", NA: "" },
    nonExecutiveManagement: { groupA: "", groupB: "", groupC: "", NA: "" },
    professionals: { groupA: "", groupB: "", groupC: "", NA: "" },
    allOtherEmployees: { groupA: "", groupB: "", groupC: "", NA: "" },
  };
  const defaultWorkforceGenderRows = [
    {
      category: "Executive Management",
      female: "",
      male: "",
      notDisclosed: "",
    },
    {
      category: "Non-Executive Management",
      female: "",
      male: "",
      notDisclosed: "",
    },
    { category: "Technical Employees", female: "", male: "", notDisclosed: "" },
    { category: "All Other Employees", female: "", male: "", notDisclosed: "" },
  ];

  const defaultDiversityGroupRows = [
    {
      category: "Executive Management",
      groupA: "",
      groupB: "",
      groupC: "",
      notDisclosed: "",
    },
    {
      category: "Non-Executive Management",
      groupA: "",
      groupB: "",
      groupC: "",
      notDisclosed: "",
    },
    {
      category: "Technical Employees",
      groupA: "",
      groupB: "",
      groupC: "",
      notDisclosed: "",
    },
    {
      category: "All Other Employees",
      groupA: "",
      groupB: "",
      groupC: "",
      notDisclosed: "",
    },
  ];
  const fleetFuelTableData = [
    { region: "", unit: "", averageFleetFuelEconomy: "" },
    { region: "", unit: "", averageFleetFuelEconomy: "" },
  ];

  const defaultMortgageRows = [
    {
      item: "Combined fixed- and variable-rate (rate resets of less than 5 years)",
      number: "",
      loanValue: "",
    },
    { item: "Prepayment Penalty", number: "", loanValue: "" },
    { item: "Total Residential Mortgages", number: "", loanValue: "" },
  ];

  const defaultMortgageResolutionRows = [
    { item: "Modification", number: "", loanValue: "" },
    { item: "Foreclosure", number: "", loanValue: "" },
    { item: "Short sale/deed in lieu", number: "", loanValue: "" },
  ];

  const defaultBorrowerTypeRows = [
    {
      borrowerType: "Minority Borrowers",
      numberOfLoans: "",
      totalLoanValue: "",
      weightedAvgLTV: "",
    },
    {
      borrowerType: "All Other Borrowers",
      numberOfLoans: "",
      totalLoanValue: "",
      weightedAvgLTV: "",
    },
  ];
  const defaultProjectDelayRows = [
    {
      projectName: "",
      numberOfDelays: "",
      totalDurationDays: "",
      rootCause: "",
      correctiveActions: "",
      status: "",
    },
  ];

  const defaultRiskOpportunityData = [
    {
      sno: 1,
      ro: "",
      description: "",
      timeHorizon: "",
      timeHorizonDefinition: "",
      comments: "",
    },
  ];

  const defaultOpportunityEffectsData = [
    {
      sno: 1,
      ro: "",
      currentEffects: "",
      anticipatedEffects: "",
      areaOfConcentration: "",
    },
  ];

  const defaultRiskActionsProgressData = [
    {
      sno: 1,
      riskOrOpportunity: "",
      actionsTakenPast: "",
      plannedActionsFuture: "",
      progressSinceLast: "",
    },
  ];

  const defaultFinancialEffectsData = [
    {
      sno: 1,
      riskOrOpportunityFE: "",
      effectsFinancialPosition: "",
      effectsFinancialPerformance: "",
      effectsCashFlows: "",
      significantRiskMaterialAdjustment: "",
    },
  ];

  const defaultTimeHorizonABCTableData = [
    {
      sno: 1,
      riskOrOpportunityABC: "",
      shortA: "",
      shortB: "",
      shortC: "",
      mediumA: "",
      mediumB: "",
      mediumC: "",
      longA: "",
      longB: "",
      longC: "",
    },
  ];

  const defaultSustainabilityRiskAssessData = [
    {
      sno: 1,
      sustainabilityRelatedRiskSR: "",
      timeHorizonSMLSR: "",
      qualitativeAssessmentSR: "",
      quantitativeAssessmentSR: "",
      assessmentMethodSR: "",
    },
  ];

  const defaultTargetsDisclosureData = [
    {
      sno: 1,
      targetNameIFRS: "",
      defineTargetIFRS: "",
      metricUsedMonitorIFRS: "",
      specifyQuantQualTargetIFRS: "",
      periodIFRS: "",
      basePeriodMeasuredIFRS: "",
      milestonesInterimTargetsIFRS: "",
      performanceTrendsIFRS: "",
      targetRevisionsIFRS: "",
    },
  ];

  const defaultClimateRiskMatrixData = [
    // Physical risks
    {
      categoryCRM: "Physical risks",
      riskCRM: "Increased Frequency of Extreme Weather Events",
      potentialImpactsCRM:
        "Supply Chain Disruptions, Facility Damage, Increased Insurance Costs",
      timePeriodCRM: "",
      applicableCRM: "",
      additionalDetailsCRM: "",
      isOtherRowCRM: false,
    },
    {
      categoryCRM: "Physical risks",
      riskCRM: "Rising Sea Levels",
      potentialImpactsCRM:
        "Property Damage, Need for Relocation, Increased Infrastructure Costs",
      timePeriodCRM: "",
      applicableCRM: "",
      additionalDetailsCRM: "",
      isOtherRowCRM: false,
    },
    {
      categoryCRM: "Physical risks",
      riskCRM: "Changes in Precipitation Patterns",
      potentialImpactsCRM:
        "Water Scarcity, Operational Delays, Crop Yield Impacts",
      timePeriodCRM: "",
      applicableCRM: "",
      additionalDetailsCRM: "",
      isOtherRowCRM: false,
    },
    {
      categoryCRM: "Physical risks",
      riskCRM: "Heatwaves or Increased temperature",
      potentialImpactsCRM:
        "Higher Energy Costs, Reduced Productivity, Equipment Overheating",
      timePeriodCRM: "",
      applicableCRM: "",
      additionalDetailsCRM: "",
      isOtherRowCRM: false,
    },
    {
      categoryCRM: "Physical risks",
      riskCRM: "Others (specify)",
      potentialImpactsCRM: "",
      timePeriodCRM: "",
      applicableCRM: "",
      additionalDetailsCRM: "",
      isOtherRowCRM: true,
    },

    // Transitional risks
    {
      categoryCRM: "Transitional risks",
      riskCRM: "Regulatory changes (e.g., carbon taxes, ESG requirements)",
      potentialImpactsCRM:
        "Increased compliance costs, penalties, adjustments in operations",
      timePeriodCRM: "",
      applicableCRM: "",
      additionalDetailsCRM: "",
      isOtherRowCRM: false,
    },
    {
      categoryCRM: "Transitional risks",
      riskCRM: "Market shifts toward low-carbon products",
      potentialImpactsCRM:
        "Declining demand for carbon-intensive products, need for innovation",
      timePeriodCRM: "",
      applicableCRM: "",
      additionalDetailsCRM: "",
      isOtherRowCRM: false,
    },
    {
      categoryCRM: "Transitional risks",
      riskCRM: "Technology advancements",
      potentialImpactsCRM:
        "Write-off of current assets and systems, capital investment in newer technology",
      timePeriodCRM: "",
      applicableCRM: "",
      additionalDetailsCRM: "",
      isOtherRowCRM: false,
    },
    {
      categoryCRM: "Transitional risks",
      riskCRM: "Investor/consumer pressure for sustainability",
      potentialImpactsCRM:
        "Brand reputation risks, reduced investor interest, loss of market share",
      timePeriodCRM: "",
      applicableCRM: "",
      additionalDetailsCRM: "",
      isOtherRowCRM: false,
    },
    {
      categoryCRM: "Transitional risks",
      riskCRM: "Others (specify)",
      potentialImpactsCRM: "",
      timePeriodCRM: "",
      applicableCRM: "",
      additionalDetailsCRM: "",
      isOtherRowCRM: true,
    },
  ];

  const defaultAnticipatedFinancialEffectsData = [
    {
      categoryAFE: "Financial position",
      currentFinancialEffectsAFE: "",
      anticipatedShortAFE: "",
      anticipatedMediumAFE: "",
      anticipatedLongAFE: "",
    },
    {
      categoryAFE: "Financial performance",
      currentFinancialEffectsAFE: "",
      anticipatedShortAFE: "",
      anticipatedMediumAFE: "",
      anticipatedLongAFE: "",
    },
    {
      categoryAFE: "Cash flows",
      currentFinancialEffectsAFE: "",
      anticipatedShortAFE: "",
      anticipatedMediumAFE: "",
      anticipatedLongAFE: "",
    },
  ];

  const defaultElementsConsideredEC = [
    {
      elementLabelEC: "Physical risks (e.g., flooding, droughts)",
      yesNoEC: "",
      explanationEC: "",
    },
    {
      elementLabelEC: "Transitional risks (e.g., policy changes, carbon tax)",
      yesNoEC: "",
      explanationEC: "",
    },
    {
      elementLabelEC: "Climate-related opportunities (e.g., green products)",
      yesNoEC: "",
      explanationEC: "",
    },
    {
      elementLabelEC: "Regional / local variables (e.g., infrastructure)",
      yesNoEC: "",
      explanationEC: "",
    },
    {
      elementLabelEC: "Policy and macroeconomic trends",
      yesNoEC: "",
      explanationEC: "",
    },
    {
      elementLabelEC: "Energy mix assumptions",
      yesNoEC: "",
      explanationEC: "",
    },
    {
      elementLabelEC: "Technological changes",
      yesNoEC: "",
      explanationEC: "",
    },
  ];

  const defaultEmissionsMeasurementTE = [
    {
      typeLabelTE: "Scope 1",
      amountMetricTonnesTE: "",
      methodUsedTE: "",
      measurementApproachTE: "",
    },
    {
      typeLabelTE: "Scope 2 (location-based)",
      amountMetricTonnesTE: "",
      methodUsedTE: "",
      measurementApproachTE: "",
    },
    {
      typeLabelTE: "Scope 2 (market-based)",
      amountMetricTonnesTE: "",
      methodUsedTE: "",
      measurementApproachTE: "",
    },
    {
      typeLabelTE: "Scope 3",
      amountMetricTonnesTE: "",
      methodUsedTE: "",
      measurementApproachTE: "",
    },
  ];

  const defaultEmissionsByTypeERO = [
    { typeLabelERO: "Transition risks", amountMetricTonnesERO: "" },
    { typeLabelERO: "Physical risks", amountMetricTonnesERO: "" },
    {
      typeLabelERO: "Climate-related opportunities",
      amountMetricTonnesERO: "",
    },
  ];

  const defaultTargetDetailsTDT = [
    {
      targetDescriptionTDT: "",
      metricUsedTDT: "",
      scopeCoveredTDT: "",
      timePeriodTDT: "",
      baseYearTDT: "",
      interimMilestonesTDT: "",
      targetTypeTDT: "",
    },
  ];

  const [assetValueTable, setAssetValueTable] = useState(assetValueData);
  const [strikesTable, setStrikesTable] = useState(strikesTabledata);
  const [forestLandTable, setForestLandTable] = useState(forestLandTableData);
  const [employeeCategoryTable, setEmployeeCategoryTable] = useState(
    EmployeeCategoryTableData
  );
  const [employeeCategoryTable1, setEmployeeCategoryTable1] = useState(
    EmployeeCategoryTableData1
  );
  const [pollutantEmissionTable, setPollutantEmissionTable] = useState(
    PollutantEmissionData
  );
  const [workforceTable1, setWorkforceTable1] = useState(workforceTableData1);

  const [steelProductionTable, setSteelProductionTable] = useState(
    rawSteelProductionData
  );
  const [corruptionTable, setCorruptionTable] = useState(corruptionTableData);
  const [initiativeTable, setInitiativeTable] = useState(initiativeTableData);
  const [drugTable, setDrugTable] = useState(drugTableData);
  const [turnoverTable, setTurnoverTable] = useState(turnoverTableData);
  const [workforceTable, setWorkforceTable] = useState(workforceTableData);
  const [processSafetyTable, setProcessSafetyTable] =
    useState(processSafetyData);
  const [marketReservedTable, setmarketReservedTable] = useState(
    marketServedTableData
  );
  const [supplyChainTable, setSupplyChainTable] =
    useState(supplyChainTableData);
  const [downstreamTable, setDownstreamTable] = useState(downstreamTableData);
  const [lawRegulationTable, setLawRegulationTable] = useState(
    lawRegulationTableData
  );
  const [materialTopicsTable, setMaterialTopicsTable] = useState(
    materialTopicsTableData
  );

  const [resilienceTable, setResilienceTable] = useState(resilienceTableData);

  const [riskTable, setRiskTable] = useState(riskTableData);

  const materialityQuestions = useSelector((state) => {
    const data = state.emission.getStandardsQuestionnaire.data.questions_list;
    return Array.isArray(data) ? data : [];
  });

  const saveQuestionnaire = useSelector(
    (state) => state.assessment.saveStandardsQuestionnaire
  );

  const submitQuestionnaire = useSelector(
    (state) => state.assessment.submitStandardsQuestionnaire
  );

  const [showSaveSubmit, setShowSaveSubmit] = useState(false);

  const [employeeTable, setEmployeeTable] = useState(defaultEmployeeData);
  const [employeeTable2, setEmployeeTable2] = useState(defaultEmployeeData1);
  const [shareholdingTable, setShareholdingTable] = useState(defaultShareData);
  const [grievanceTable, setGrievanceTable] = useState(defaultGrievanceData);
  const [materialTable, setMaterialTable] = useState(defaultMaterialData);
  const [disclosureData, setDisclosureData] = useState(defaultDisclosureData);
  const [reviewData, setReviewData] = useState(defaultReviewRows);
  const [frequencyData, setFrequencyData] = useState(
    defaultFrequencyReviewRows
  );
  const [reasonData, setReasonData] = useState(defaultReasonRows);
  const [trainingData, setTrainingData] = useState(defaultTrainingData);
  const [legalData, setLegalData] = useState(defaultLegalActions);
  const [caseStudies, setCaseStudies] = useState(defaultCaseStudiesData);
  const [data, setData] = useState(defaultRows);
  const [conflictData, setConflictData] = useState(defaultConflictRows);
  const [payableData, setPayableData] = useState(defaultAccountRows);
  const [concentrationtableData, setConcentrationTableData] = useState(
    defaultConcentrationData
  );
  const [awarenessData, setAwarenessData] = useState(defaultAwarenessData);
  const [improvementData, setImprovementData] = useState(
    defaultImprovementRows
  );
  const [improvementData1, setImprovementData1] = useState(
    defaultImprovementRows1
  );
  const [productrows, setProductRows] = useState(defaultProductData);
  const [recycledData, setRecycledData] = useState(defaultrecycledData);
  const [wastedata, setWasteData] = useState(defaultWasteData);
  const [packagedata, setPackageData] = useState(defaultPackageData);
  const [benifitTable, setBenifitTable] = useState(defaultBenefitData);
  const [workersbenefitTable, setWorkersBenefitTable] = useState(
    defaultWorkersBenefitData
  );
  const [wellbeingdata, setWellbeingData] = useState(defaultWellbeingRows);
  const [statutorydata, setStatutoryData] = useState(defaultStatutoryTypes);
  const [returndata, setReturnData] = useState(defaultgenders);
  const [reclaimedproductdata, setReclaimedProductData] =
    useState(defaultcategories);
  const [uniondata, setUnionData] = useState(defaultUnionData);
  const [trainingmeasuresdata, setTrainingMeasuresData] = useState(
    defaultTrainingmeasuresData
  );
  const [fydata, setFyData] = useState(defaultFYData);
  const [safetyData, setSafetyData] = useState(incidentTypes);
  const [pfdata, setPfData] = useState(defaultPFData);
  const [assessmentData, setAssessmentData] = useState(defaultAssessmentData);
  const [rehabdata, setRehabData] = useState(defaultRehabData);
  const [valuedata, setValueData] = useState(defaultValuechainData);
  const [stakeholderdata, setStakeholderData] = useState(
    defaultStakeholderData
  );
  const [coveragedata, setCoverageData] = useState(defaultCoverageData);
  const [wageData, setWageData] = useState(defaultsections);
  const [remunerationdata, setRemunerationData] = useState(defaultRoles);
  const [femaleWageData, setFemaleWageData] = useState(defaultFemaleWage);
  const [humanrightsdata, setHumanrightsData] = useState(
    defaultrightscategories
  );
  const [poshData, setPoshData] = useState(poshComplaintRows);
  const [plantAssessmentData, setPlantAssessmentData] = useState(
    defaultAssessmentIssues
  );
  const [valueChainAssessmentData, setValueChainAssessmentData] = useState(
    defaultValueChainIssues
  );
  const [energyData, setEnergyData] = useState(defaultEnergyData);
  const [waterData, setWaterData] = useState(defaultWaterData);
  const [dischargeData, setDischargeData] = useState(dischargeCategories);
  const [airdata, setAirData] = useState(defaultAirPollutantData);
  const [scopeData, setScopeData] = useState(defaultscopeparameters);
  const [wasteManagementData, setWasteManagementData] =
    useState(wasteParameters);

  const [envclearanceData, setEnvClearanceData] = useState(
    defaultenvclearanceData
  );
  const [eiaData, setEiaData] = useState(defaultEIAData);
  const [nonComplianceData, setNonComplianceData] = useState(
    defaultregulatoryData
  );
  const [waterusagedata, setWaterUsageData] = useState(defaultusageWaterData);
  const [scope3Data, setScope3Data] = useState(defaultScope3Data);
  const [initiativeData, setInitiativeData] = useState(defaultInitiativeData);
  const [associations, setAssociations] = useState(defaultTradeData);
  const [authorityData, setAuthorityData] = useState(defaultAuthorityData);
  const [advocacyData, setAdvocacyData] = useState(defaultAdvocacyData);
  const [siaData, setSiaData] = useState(defaultSiaData);
  const [resettlementData, setResettlementData] = useState(
    defaultResettlementData
  );
  const [sourcingData, setSourcingData] = useState(defaultSourcingData);
  const [locationData, setLocationData] = useState(defaultLocationData);
  const [socialImpactData, setSocialImpactData] = useState(
    defaultSocialImpactData
  );
  const [aspirationalDistrictData, setAspirationalDistrictData] = useState(
    defaultAspirationalDistrictData
  );
  const [traditionalKnowledgeData, setTraditionalKnowledgeData] = useState(
    defaultTraditionalKnowledgeData
  );
  const [regulatoryCaseData, setRegulatoryCaseData] = useState(
    defaultRegulatoryCaseData
  );
  const [csrBeneficiaryData, setCsrBeneficiaryData] = useState(
    defaultCsrBeneficiaryData
  );
  const [turnoverData, setTurnoverData] = useState(defaultTurnoverData);
  const [complaintData, setComplaintData] = useState(
    defaultCustomerComplaintData
  );
  const [recallData, setRecallData] = useState(defaultRecallData);

  const [oversightFormTable, setoversightFormTable] = useState(
    defaultOversightFormData
  );
  const [dueDiligenceData, setDueDiligenceData] = useState(
    defaultDueDiligenceData
  );

  const [resourceData, setResourceData] = useState(
    defaultResourceAllocationData
  );
  const [targetsData, setTargetsData] = useState(
    defaultSustainabilityTargetsData
  );

  const [policyData, setPolicyData] = useState(defaultPolicyTableData);

  const [genderRepresentationTabel, setGenderRepresentationTabel] = useState(
    defaultGenderRepresentationData
  );
  const [groupRepresentationTabel, setGroupRepresentationTabel] = useState(
    defaultGroupRepresentationData
  );
  const [workforceGenderTable, setWorkforceGenderTable] = useState(
    defaultWorkforceGenderRows
  );

  const [diversityGroupTable, setDiversityGroupTable] = useState(
    defaultDiversityGroupRows
  );
  const [fleetTable, setFleetTable] = useState(fleetFuelTableData);
  const [mortgageTable, setMortgageTable] = useState(defaultMortgageRows);
  const [resolutionTable, setResolutionTable] = useState(
    defaultMortgageResolutionRows
  );
  const [borrowerTypeTable, setBorrowerTypeTable] = useState(
    defaultBorrowerTypeRows
  );
  const [delaysTable, setDelaysTable] = useState(defaultProjectDelayRows);

  const [riskOpportunityData, setRiskOpportunityData] = useState(
    defaultRiskOpportunityData
  );

  const [opportunityData, setOpportunityData] = useState(
    defaultOpportunityEffectsData
  );

  const [riskActionsProgressData, setRiskActionsProgressData] = useState(
    defaultRiskActionsProgressData
  );
  const [financialEffectsData, setFinancialEffectsData] = useState(
    defaultFinancialEffectsData
  );
  const [timeHorizonABCTableData, setTimeHorizonABCTableData] = useState(
    defaultTimeHorizonABCTableData
  );

  const [sustainabilityRiskAssessData, setSustainabilityRiskAssessData] =
    useState(defaultSustainabilityRiskAssessData);

  const [targetsDisclosureData, setTargetsDisclosureData] = useState(
    defaultTargetsDisclosureData
  );

  const [climateRiskMatrixData, setClimateRiskMatrixData] = useState(
    defaultClimateRiskMatrixData
  );

  const [anticipatedFinancialEffectsData, setAnticipatedFinancialEffectsData] =
    useState(defaultAnticipatedFinancialEffectsData);

  const [elementsConsideredEC, setElementsConsideredEC] = useState(
    defaultElementsConsideredEC
  );
  const [emissionsMeasurementTE, setEmissionsMeasurementTE] = useState(
    defaultEmissionsMeasurementTE
  );
  const [emissionsByTypeERO, setEmissionsByTypeERO] = useState(
    defaultEmissionsByTypeERO
  );

  const [targetDetailsTDT, setTargetDetailsTDT] = useState(
    defaultTargetDetailsTDT
  );
  // Helper to convert number to Roman numeral
  function toRoman(num) {
    const romanMap = {
      1: "I",
      2: "II",
      3: "III",
      4: "IV",
      5: "V",
      6: "VI",
      7: "VII",
      8: "VIII",
      9: "IX",
      10: "X",
      11: "XI",
      12: "XII",
      13: "XIII",
      14: "XIV",
      15: "XV",
    };
    return romanMap[num] || num;
  }

  const assessmentList = useSelector(
    (state) => state.assessment.listAssessmentCycleCountry.data
  );

  const frameworkList = useSelector(
    (state) => state.listings.frameworkList?.data
  );

  const countryData = useSelector(
    (state) => state.listings.getOrganizationCountry.data.country_data
  );

  const editAnswerData = useSelector(
    (item) => item.assessment.getStandardsQuestionnaireAnswers.data
  );
  const editAnswerDataStatus = useSelector(
    (item) => item.assessment.getStandardsQuestionnaireAnswers.status
  );

  const filteredList =
    assessmentList?.filter(
      (item) => item.assessment_year === year && item.country === country
    ) || [];

  const transformedYearList = [
    { key: null, value: "Select Option" },
    ...(assessmentList
      ?.filter((item) => item.country === country)
      .map((item) => ({
        key: item.assessment_year,
        value: `${item.assessment_year}`,
      })) || []),
  ];

  const frameworkName = filteredList[0]?.framework;
  const assessment_id = filteredList[0]?.id;
  const isSubmitAndSaveAllowed = !(
    editAnswerData?.status === "approved" ||
    (editAnswerData?.status === "submitted" &&
      role !== "sustainability_manager")
  );
  var assessmentPeriod = "";
  if (frameworkName?.toLowerCase() === "gri") {
    var startYear = filteredList[0]?.assessment_year;
    if (
      filteredList[0]?.assessment_start_month >
      filteredList[0]?.assessment_end_month
    )
      startYear -= 1;
    assessmentPeriod = `${
      monthNames[filteredList[0]?.assessment_start_month - 1]
    } ${startYear} - ${monthNames[filteredList[0]?.assessment_end_month - 1]} ${
      filteredList[0]?.assessment_year
    }`;
  }

  const countryMap = {};
  let countryOptions = [];
  for (const [key, value] of Object.entries(countryData)) {
    countryMap[value] = key;
    countryOptions = countryOptions.concat({ key: value, value: key });
  }

  useEffect(() => {
    dispatch(listAssessmentCycle());
    dispatch(listFramework());
    dispatch(getStandardsQuestionnaireHistory());
  }, [dispatch]);

  useEffect(() => {
    dispatch(listAssessmentCycleCountry({ country }));
  }, [dispatch, country]);

  const frameworkId = frameworkList.find(
    (item) => item.name === frameworkName
  )?.id;

  const handleYearChange = (event) => {
    setYear(event.target.value);
  };

  const handleCountryChange = (event) => {
    setCountry(event.target.value);
    setYear(null);
  };

  useEffect(() => {
    if (frameworkId) {
      dispatch(
        getStandardsQuestionnaireAnswers({
          framework_id: frameworkId,
          country: country,
          year: year,
        })
      );
    }
  }, [dispatch, frameworkId, year, country]);

  useEffect(() => {
    if (editAnswerData && Object.keys(editAnswerData).length > 0) {
      const { answer } = editAnswerData;
      const temp = answer?.find(
        (item) =>
          item.condition === "table_sasb_iron_steel_1" ||
          item.condition === "table_sasb_chemicals_1"
      )?.answer;

      setSelectedValues(
        (answer || []).reduce((acc, item) => {
          const isMultiSelect = item.question_type === "multiselect_check_box";

          if (isMultiSelect) {
            let val = item.answer;

            if (typeof val === "string") {
              val = val.split(/,\s(?=[A-Z])/).map((opt) => opt.trim());
            }

            acc[item.question_id] = [...new Set(val)];
          } else {
            acc[item.question_id] = item.answer || "";
          }

          return acc;
        }, {})
      );

      setTextAnswers(
        (answer || []).reduce((acc, item) => {
          if (item.question_type === "multiselect_check_box") {
            if (item.other) {
              acc[`other-${item.question_id}`] = item.other;
            }

            const options = [
              "Warning letters:",
              "Seizures:",
              "Recalls due to GMP:",
              "Consent decrees:",
              "Other (Please specify):",
            ];

            options.forEach((label) => {
              if (item?.[label]) {
                acc[`other-${item.question_id}-${label}`] = item[label];
              }
            });
          }
          if (item.question_type === "choose_from_option" && item.other) {
            acc[`other-${item.question_id}`] = item.other;
          }
          if (
            item.question_type === "text" ||
            item.question_type === "short_text" ||
            item.question_type === "number_text" ||
            item.question_type === "drop_down"
          ) {
            acc[item.question_id] = item.answer;
          }
          return acc;
        }, {})
      );

      setDateAnswers(
        (answer || []).reduce((acc, item) => {
          if (item.other) acc[`other-${item.question_id}`] = item.other;
          if (item.question_type === "date")
            acc[item.question_id] = dayjs(item.answer, "DD/MM/YYYY");
          return acc;
        }, {})
      );

      const tableRes = answer?.find(
        (item) => item.question_id === "677770f39e7e8a6081643369"
      )?.answer;
      const resilienceTableRes = answer?.find(
        (item) => item.question_id === "677770f39e7e8a6081643379"
      )?.answer;

      if (tableRes) setRiskTable(tableRes);
      if (resilienceTableRes) setResilienceTable(resilienceTableRes);
      setAssetValueTable(
        answer?.find((item) => item.condition === "table_gri_1")?.answer ||
          assetValueTable
      );
      setmarketReservedTable(
        answer?.find((item) => item.condition === "table_gri_2")?.answer ||
          marketReservedTable
      );
      setSupplyChainTable(
        answer?.find((item) => item.condition === "table_gri_3")?.answer ||
          supplyChainTable
      );
      setDownstreamTable(
        answer?.find((item) => item.condition === "table_gri_4")?.answer ||
          downstreamTable
      );
      setLawRegulationTable(
        answer?.find((item) => item.condition === "table_gri_5")?.answer ||
          lawRegulationTable
      );
      setMaterialTopicsTable(
        answer?.find((item) => item.condition === "table_gri_6")?.answer ||
          materialTopicsTable
      );
      setCorruptionTable(
        answer?.find(
          (item) => item.condition === "table_sasb_aerospace_&_defence_1"
        )?.answer || corruptionTable
      );
      setInitiativeTable(
        answer?.find((item) => item.condition === "table_sasb_biotechnology_1")
          ?.answer || initiativeTable
      );
      setDrugTable(
        answer?.find((item) => item.condition === "table_sasb_biotechnology_2")
          ?.answer || drugTable
      );
      setTurnoverTable(
        answer?.find((item) => item.condition === "table_sasb_biotechnology_3")
          ?.answer || turnoverTable
      );
      setForestLandTable(
        answer?.find(
          (item) => item.condition === "table_sasb_forestry_management_1"
        )?.answer || forestLandTable
      );
      setStrikesTable(
        answer?.find((item) => item.condition === "table_sasb_airlines_1")
          ?.answer || strikesTable
      );
      setEmployeeCategoryTable(
        answer?.find(
          (item) => item.condition === "table_sasb_advertising_&_marketing_1"
        )?.answer || employeeCategoryTable
      );
      setEmployeeCategoryTable1(
        answer?.find(
          (item) => item.condition === "table_sasb_advertising_&_marketing_2"
        )?.answer || employeeCategoryTable1
      );
      setPollutantEmissionTable(
        answer?.find(
          (item) =>
            item.condition === "table_sasb_iron_steel_1" ||
            item.condition === "table_sasb_chemicals_1"
        )?.answer || pollutantEmissionTable
      );
      setWorkforceTable(
        answer?.find(
          (item) =>
            item.condition === "table_sasb_iron_steel_2" ||
            item.condition === "table_sasb_engineering_&_construction_service_1"
        )?.answer || workforceTable
      );
      setSteelProductionTable(
        answer?.find((item) => item.condition === "table_sasb_iron_steel_3")
          ?.answer || steelProductionTable
      );

      setWorkforceTable1(
        answer?.find((item) => item.condition === "table_sasb_chemicals_2")
          ?.answer || workforceTable1
      );
      setProcessSafetyTable(
        answer?.find((item) => item.condition === "table_sasb_chemicals_3")
          ?.answer || processSafetyTable
      );
      setGenderRepresentationTabel(
        answer?.find(
          (item) => item.condition === "table_sasb_media_&_entertainment_1"
        )?.answer || genderRepresentationTabel
      );
      setGroupRepresentationTabel(
        answer?.find(
          (item) => item.condition === "table_sasb_media_&_entertainment_2"
        )?.answer || groupRepresentationTabel
      );
      setEmployeeTable(
        answer?.find((item) => item.condition === "brsr_table_1")?.answer ||
          employeeTable
      );
      setEmployeeTable2(
        answer?.find((item) => item.condition === "brsr_table_2")?.answer ||
          employeeTable2
      );
      setShareholdingTable(
        answer?.find((item) => item.condition === "brsr_table_3")?.answer ||
          shareholdingTable
      );
      setGrievanceTable(
        answer?.find((item) => item.condition === "brsr_table_4")?.answer ||
          grievanceTable
      );
      setMaterialTable(
        answer?.find((item) => item.condition === "brsr_table_5")?.answer ||
          materialTable
      );
      setDisclosureData(
        answer?.find((item) => item.condition === "brsr_table_6")?.answer ||
          disclosureData
      );
      setReviewData(
        answer?.find((item) => item.condition === "brsr_table_7")?.answer ||
          reviewData
      );
      setFrequencyData(
        answer?.find((item) => item.condition === "brsr_table_8")?.answer ||
          frequencyData
      );
      setReasonData(
        answer?.find((item) => item.condition === "brsr_table_9")?.answer ||
          reasonData
      );
      setTrainingData(
        answer?.find((item) => item.condition === "brsr_table_10")?.answer ||
          trainingData
      );
      setLegalData(
        answer?.find((item) => item.condition === "brsr_table_11")?.answer ||
          legalData
      );
      setCaseStudies(
        answer?.find((item) => item.condition === "brsr_table_12")?.answer ||
          caseStudies
      );
      setData(
        answer?.find((item) => item.condition === "brsr_table_13")?.answer ||
          data
      );
      setConflictData(
        answer?.find((item) => item.condition === "brsr_table_14")?.answer ||
          conflictData
      );
      setPayableData(
        answer?.find((item) => item.condition === "brsr_table_15")?.answer ||
          payableData
      );
      setConcentrationTableData(
        answer?.find((item) => item.condition === "brsr_table_16")?.answer ||
          concentrationtableData
      );
      setAwarenessData(
        answer?.find((item) => item.condition === "brsr_table_17")?.answer ||
          awarenessData
      );
      setImprovementData(
        answer?.find((item) => item.condition === "brsr_table_18")?.answer ||
          improvementData
      );
      setImprovementData1(
        answer?.find((item) => item.condition === "brsr_table_19")?.answer ||
          improvementData1
      );
      setProductRows(
        answer?.find((item) => item.condition === "brsr_table_20")?.answer ||
          productrows
      );
      setRecycledData(
        answer?.find((item) => item.condition === "brsr_table_21")?.answer ||
          recycledData
      );
      setWasteData(
        answer?.find((item) => item.condition === "brsr_table_22")?.answer ||
          wastedata
      );
      setPackageData(
        answer?.find((item) => item.condition === "brsr_table_23")?.answer ||
          packagedata
      );
      setBenifitTable(
        answer?.find((item) => item.condition === "brsr_table_24")?.answer ||
          benifitTable
      );
      setWorkersBenefitTable(
        answer?.find((item) => item.condition === "brsr_table_25")?.answer ||
          workersbenefitTable
      );
      setWellbeingData(
        answer?.find((item) => item.condition === "brsr_table_26")?.answer ||
          wellbeingdata
      );
      setStatutoryData(
        answer?.find((item) => item.condition === "brsr_table_27")?.answer ||
          statutorydata
      );
      setReturnData(
        answer?.find((item) => item.condition === "brsr_table_28")?.answer ||
          returndata
      );
      setReclaimedProductData(
        answer?.find((item) => item.condition === "brsr_table_29")?.answer ||
          reclaimedproductdata
      );
      setUnionData(
        answer?.find((item) => item.condition === "brsr_table_30")?.answer ||
          uniondata
      );
      setTrainingMeasuresData(
        answer?.find((item) => item.condition === "brsr_table_31")?.answer ||
          trainingmeasuresdata
      );
      setFyData(
        answer?.find((item) => item.condition === "brsr_table_32")?.answer ||
          fydata
      );
      setSafetyData(
        answer?.find((item) => item.condition === "brsr_table_33")?.answer ||
          safetyData
      );
      setPfData(
        answer?.find((item) => item.condition === "brsr_table_34")?.answer ||
          pfdata
      );
      setAssessmentData(
        answer?.find((item) => item.condition === "brsr_table_35")?.answer ||
          assessmentData
      );
      setRehabData(
        answer?.find((item) => item.condition === "brsr_table_36")?.answer ||
          rehabdata
      );
      setValueData(
        answer?.find((item) => item.condition === "brsr_table_37")?.answer ||
          valuedata
      );
      setStakeholderData(
        answer?.find((item) => item.condition === "brsr_table_38")?.answer ||
          stakeholderdata
      );
      setCoverageData(
        answer?.find((item) => item.condition === "brsr_table_39")?.answer ||
          coveragedata
      );
      setWageData(
        answer?.find((item) => item.condition === "brsr_table_40")?.answer ||
          wageData
      );
      setRemunerationData(
        answer?.find((item) => item.condition === "brsr_table_41")?.answer ||
          remunerationdata
      );
      setFemaleWageData(
        answer?.find((item) => item.condition === "brsr_table_42")?.answer ||
          femaleWageData
      );
      setHumanrightsData(
        answer?.find((item) => item.condition === "brsr_table_43")?.answer ||
          humanrightsdata
      );
      setPoshData(
        answer?.find((item) => item.condition === "brsr_table_44")?.answer ||
          poshData
      );
      setPlantAssessmentData(
        answer?.find((item) => item.condition === "brsr_table_45")?.answer ||
          plantAssessmentData
      );
      setValueChainAssessmentData(
        answer?.find((item) => item.condition === "brsr_table_46")?.answer ||
          valueChainAssessmentData
      );
      setEnergyData(
        answer?.find((item) => item.condition === "brsr_table_47")?.answer ||
          energyData
      );
      setWaterData(
        answer?.find((item) => item.condition === "brsr_table_48")?.answer ||
          waterData
      );
      setDischargeData(
        answer?.find((item) => item.condition === "brsr_table_49")?.answer ||
          dischargeData
      );
      setAirData(
        answer?.find((item) => item.condition === "brsr_table_50")?.answer ||
          airdata
      );
      setScopeData(
        answer?.find((item) => item.condition === "brsr_table_51")?.answer ||
          scopeData
      );
      setWasteManagementData(
        answer?.find((item) => item.condition === "brsr_table_52")?.answer ||
          wasteManagementData
      );
      setEnvClearanceData(
        answer?.find((item) => item.condition === "brsr_table_53")?.answer ||
          envclearanceData
      );
      setEiaData(
        answer?.find((item) => item.condition === "brsr_table_54")?.answer ||
          eiaData
      );
      setNonComplianceData(
        answer?.find((item) => item.condition === "brsr_table_55")?.answer ||
          nonComplianceData
      );
      setWaterUsageData(
        answer?.find((item) => item.condition === "brsr_table_56")?.answer ||
          waterusagedata
      );
      setScope3Data(
        answer?.find((item) => item.condition === "brsr_table_57")?.answer ||
          scope3Data
      );
      setInitiativeData(
        answer?.find((item) => item.condition === "brsr_table_58")?.answer ||
          initiativeData
      );
      setAssociations(
        answer?.find((item) => item.condition === "brsr_table_59")?.answer ||
          associations
      );
      setAuthorityData(
        answer?.find((item) => item.condition === "brsr_table_60")?.answer ||
          authorityData
      );
      setAdvocacyData(
        answer?.find((item) => item.condition === "brsr_table_61")?.answer ||
          advocacyData
      );
      setSiaData(
        answer?.find((item) => item.condition === "brsr_table_62")?.answer ||
          siaData
      );
      setResettlementData(
        answer?.find((item) => item.condition === "brsr_table_63")?.answer ||
          resettlementData
      );
      setSourcingData(
        answer?.find((item) => item.condition === "brsr_table_64")?.answer ||
          sourcingData
      );
      setLocationData(
        answer?.find((item) => item.condition === "brsr_table_65")?.answer ||
          locationData
      );
      setSocialImpactData(
        answer?.find((item) => item.condition === "brsr_table_66")?.answer ||
          socialImpactData
      );
      setAspirationalDistrictData(
        answer?.find((item) => item.condition === "brsr_table_67")?.answer ||
          aspirationalDistrictData
      );
      setTraditionalKnowledgeData(
        answer?.find((item) => item.condition === "brsr_table_68")?.answer ||
          traditionalKnowledgeData
      );
      setRegulatoryCaseData(
        answer?.find((item) => item.condition === "brsr_table_69")?.answer ||
          regulatoryCaseData
      );
      setCsrBeneficiaryData(
        answer?.find((item) => item.condition === "brsr_table_70")?.answer ||
          csrBeneficiaryData
      );
      setTurnoverData(
        answer?.find((item) => item.condition === "brsr_table_71")?.answer ||
          turnoverData
      );
      setComplaintData(
        answer?.find((item) => item.condition === "brsr_table_72")?.answer ||
          complaintData
      );
      setRecallData(
        answer?.find((item) => item.condition === "brsr_table_73")?.answer ||
          recallData
      );
      setoversightFormTable(
        answer?.find((item) => item.condition === "csrd_table_1")?.answer ||
          oversightFormTable
      );
      setDueDiligenceData(
        answer?.find((item) => item.condition === "csrd_table_2")?.answer ||
          dueDiligenceData
      );
      setPolicyData(
        answer?.find((item) => item.condition === "csrd_table_3")?.answer ||
          policyData
      );
      setResourceData(
        answer?.find((item) => item.condition === "csrd_table_4")?.answer ||
          resourceData
      );
      setTargetsData(
        answer?.find((item) => item.condition === "csrd_table_5")?.answer ||
          targetsData
      );
      setWorkforceGenderTable(
        answer?.find(
          (item) => item.condition === "table_sasb_software_&_it_services_1"
        )?.answer || workforceGenderTable
      );
      setDiversityGroupTable(
        answer?.find(
          (item) => item.condition === "table_sasb_software_&_it_services_2"
        )?.answer || diversityGroupTable
      );
      setFleetTable(
        answer?.find(
          (item) => item.condition === "table_sasb_car_rental_&_leasing_1"
        )?.answer || fleetTable
      );
      setMortgageTable(
        answer?.find(
          (item) => item.condition === "table_sasb_mortgage_finance_1"
        )?.answer || mortgageTable
      );
      setResolutionTable(
        answer?.find(
          (item) => item.condition === "table_sasb_mortgage_finance_2"
        )?.answer || resolutionTable
      );
      setBorrowerTypeTable(
        answer?.find(
          (item) => item.condition === "table_sasb_mortgage_finance_3"
        )?.answer || borrowerTypeTable
      );
      setDelaysTable(
        answer?.find(
          (item) =>
            item.condition ===
            "table_sasb_solar_technology_&_project_developers"
        )?.answer || delaysTable
      );
      setRiskOpportunityData(
        answer?.find((item) => item.condition === "ifrs_table_1")?.answer ||
          riskOpportunityData
      );
      setOpportunityData(
        answer?.find((item) => item.condition === "ifrs_table_2")?.answer ||
          opportunityData
      );
      setRiskActionsProgressData(
        answer?.find((item) => item.condition === "ifrs_table_3")?.answer ||
          riskActionsProgressData
      );
      setFinancialEffectsData(
        answer?.find((item) => item.condition === "ifrs_table_4")?.answer ||
          financialEffectsData
      );
      setTimeHorizonABCTableData(
        answer?.find((item) => item.condition === "ifrs_table_5")?.answer ||
          timeHorizonABCTableData
      );
      setSustainabilityRiskAssessData(
        answer?.find((item) => item.condition === "ifrs_table_6")?.answer ||
          sustainabilityRiskAssessData
      );
      setTargetsDisclosureData(
        answer?.find((item) => item.condition === "ifrs_table_7")?.answer ||
          targetsDisclosureData
      );
      setClimateRiskMatrixData(
        answer?.find((item) => item.condition === "ifrs_table_8")?.answer ||
          climateRiskMatrixData
      );
      setAnticipatedFinancialEffectsData(
        answer?.find((item) => item.condition === "ifrs_table_9")?.answer ||
          anticipatedFinancialEffectsData
      );
      setElementsConsideredEC(
        answer?.find((item) => item.condition === "ifrs_table_10")?.answer ||
          elementsConsideredEC
      );
      setEmissionsMeasurementTE(
        answer?.find((item) => item.condition === "ifrs_table_11")?.answer ||
          emissionsMeasurementTE
      );
      setEmissionsByTypeERO(
        answer?.find((item) => item.condition === "ifrs_table_12")?.answer ||
          emissionsByTypeERO
      );
      setTargetDetailsTDT(
        answer?.find((item) => item.condition === "ifrs_table_13")?.answer ||
          targetDetailsTDT
      );
    } else {
      setSelectedValues({});
      setTextAnswers({});
      setRiskTable(riskTableData);
      setResilienceTable(resilienceTableData);
      setCorruptionTable(corruptionTableData);
      setDrugTable(drugTableData);
      setTurnoverTable(turnoverTableData);
      setInitiativeTable(initiativeTableData);
      setForestLandTable(forestLandTableData);
      setEmployeeCategoryTable(EmployeeCategoryTableData);
      setEmployeeCategoryTable1(EmployeeCategoryTableData1);
      setPollutantEmissionTable(PollutantEmissionData);
      setStrikesTable(strikesTabledata);
      setSteelProductionTable(rawSteelProductionData);
      setWorkforceTable1(workforceTableData1);
      setWorkforceTable(workforceTableData);
      setEmployeeTable(defaultEmployeeData);
      setEmployeeTable2(defaultEmployeeData1);
      setShareholdingTable(defaultShareData);
      setGrievanceTable(defaultGrievanceData);
      setMaterialTable(defaultMaterialData);
      setDisclosureData(defaultDisclosureData);
      setReviewData(defaultReviewRows);
      setFrequencyData(defaultFrequencyReviewRows);
      setReasonData(defaultReasonRows);
      setTrainingData(defaultTrainingData);
      setLegalData(defaultLegalActions);
      setCaseStudies(defaultCaseStudiesData);
      setData(defaultRows);
      setConflictData(defaultConflictRows);
      setPayableData(defaultAccountRows);
      setConcentrationTableData(defaultConcentrationData);
      setAwarenessData(defaultAwarenessData);
      setImprovementData(defaultImprovementRows);
      setImprovementData1(defaultImprovementRows1);
      setProductRows(defaultProductData);
      setRecycledData(defaultrecycledData);
      setWasteData(defaultWasteData);
      setPackageData(defaultPackageData);
      setBenifitTable(defaultBenefitData);
      setWorkersBenefitTable(defaultWorkersBenefitData);
      setWellbeingData(defaultWellbeingRows);
      setStatutoryData(defaultStatutoryTypes);
      setReturnData(defaultgenders);
      setReclaimedProductData(defaultcategories);
      setUnionData(defaultUnionData);
      setTrainingMeasuresData(defaultTrainingmeasuresData);
      setFyData(defaultFYData);
      setSafetyData(incidentTypes);
      setPfData(defaultPFData);
      setAssessmentData(defaultAssessmentData);
      setRehabData(defaultRehabData);
      setValueData(defaultValuechainData);
      setStakeholderData(defaultStakeholderData);
      setCoverageData(defaultCoverageData);
      setWageData(defaultsections);
      setRemunerationData(defaultRoles);
      setFemaleWageData(defaultFemaleWage);
      setHumanrightsData(defaultrightscategories);
      setPoshData(poshComplaintRows);
      setPlantAssessmentData(defaultAssessmentIssues);
      setValueChainAssessmentData(defaultValueChainIssues);
      setEnergyData(defaultEnergyData);
      setWaterData(defaultWaterData);
      setDischargeData(dischargeCategories);
      setAirData(defaultAirPollutantData);
      setScopeData(defaultscopeparameters);
      setWasteManagementData(wasteParameters);
      setEnvClearanceData(defaultenvclearanceData);
      setEiaData(defaultEIAData);
      setNonComplianceData(defaultregulatoryData);
      setWaterUsageData(defaultusageWaterData);
      setScope3Data(defaultScope3Data);
      setInitiativeData(defaultInitiativeData);
      setAssociations(defaultTradeData);
      setAuthorityData(defaultAuthorityData);
      setAdvocacyData(defaultAdvocacyData);
      setSiaData(defaultSiaData);
      setResettlementData(defaultResettlementData);
      setSourcingData(defaultSourcingData);
      setLocationData(defaultLocationData);
      setSocialImpactData(defaultSocialImpactData);
      setAspirationalDistrictData(defaultAspirationalDistrictData);
      setTraditionalKnowledgeData(defaultTraditionalKnowledgeData);
      setRegulatoryCaseData(defaultRegulatoryCaseData);
      setCsrBeneficiaryData(defaultCsrBeneficiaryData);
      setTurnoverData(defaultTurnoverData);
      setComplaintData(defaultCustomerComplaintData);
      setRecallData(defaultRecallData);
      setoversightFormTable(defaultOversightFormData);
      setDueDiligenceData(defaultDueDiligenceData);
      setPolicyData(defaultPolicyTableData);
      setResourceData(defaultResourceAllocationData);
      setTargetsData(defaultSustainabilityTargetsData);
      setWorkforceGenderTable(defaultWorkforceGenderRows);
      setDiversityGroupTable(defaultDiversityGroupRows);
      setFleetTable(fleetFuelTableData);
      setMortgageTable(defaultMortgageRows);
      setResolutionTable(defaultMortgageResolutionRows);
      setBorrowerTypeTable(defaultBorrowerTypeRows);
      setDelaysTable(defaultProjectDelayRows);
      setRiskOpportunityData(defaultRiskOpportunityData);
      setOpportunityData(defaultOpportunityEffectsData);
      setRiskActionsProgressData(defaultRiskActionsProgressData);
      setFinancialEffectsData(defaultFinancialEffectsData);
      setTimeHorizonABCTableData(defaultTimeHorizonABCTableData);
      setSustainabilityRiskAssessData(defaultSustainabilityRiskAssessData);
      setTargetsDisclosureData(defaultTargetsDisclosureData);
      setClimateRiskMatrixData(defaultClimateRiskMatrixData);
      setAnticipatedFinancialEffectsData(
        defaultAnticipatedFinancialEffectsData
      );
      setElementsConsideredEC(defaultElementsConsideredEC);
      setEmissionsMeasurementTE(defaultEmissionsMeasurementTE);
      setEmissionsByTypeERO(defaultEmissionsByTypeERO);
      setTargetDetailsTDT(defaultTargetDetailsTDT);
      setShowSaveSubmit(false);
    }
  }, [editAnswerData]);

  useEffect(() => {
    if (frameworkId && assessment_id) {
      dispatch(getStandardsQuestionnaire(frameworkId, assessment_id));
    } else {
      console.error("frameworkId or assessmentId is missing:", {
        frameworkId,
        assessment_id,
      });
    }
  }, [dispatch, frameworkId, assessment_id]);

  useEffect(() => {
    if (saveQuestionnaire.status === STATUS.SUCCESS) {
      enqueueSnackbar("Saved Successfully !", {
        variant: "success",
      });
      dispatch(resetSaveStandardsQuestionnaire());
      navigate("/emissions/standards-questionnaire/list");
    } else if (saveQuestionnaire.status === STATUS.ERROR) {
      enqueueSnackbar(
        saveQuestionnaire.message ||
          "We couldn't process your request. Please try again later.",
        { variant: "error" }
      );
      dispatch(resetSaveStandardsQuestionnaire());
    }
  }, [saveQuestionnaire, dispatch, enqueueSnackbar]);

  useEffect(() => {
    if (submitQuestionnaire.status === STATUS.SUCCESS) {
      enqueueSnackbar("Submitted Successfully !", {
        variant: "success",
      });
      dispatch(resetSubmitStandardsQuestionnaire());
      navigate("/emissions/standards-questionnaire/list");
    } else if (submitQuestionnaire.status === STATUS.ERROR) {
      enqueueSnackbar(
        submitQuestionnaire.message.message ||
          "We couldn't process your request. Please try again later.",
        { variant: "error" }
      );
      dispatch(resetSubmitStandardsQuestionnaire());
    }
  }, [submitQuestionnaire, dispatch, enqueueSnackbar]);

  useEffect(() => {
    if (editAnswerDataStatus === STATUS.ERROR) {
      dispatch(resetStandardsQuestionnaireAnswers());
    }
  }, [editAnswerDataStatus, dispatch]);
  useEffect(() => {
    const reportingQuestion = materialityQuestions.find(
      (q) =>
        q.question_type === "short_text" &&
        q.question.trim() === "Reporting period"
    );

    if (reportingQuestion && year && !textAnswers[reportingQuestion.id]) {
      setTextAnswers((prev) => ({
        ...prev,
        [reportingQuestion.id]: assessmentPeriod,
      }));
    }
  }, [materialityQuestions, year, textAnswers]);

  const navigateBack = () => {
    navigate(-1);
  };

  const handleCheckboxChange = (event, questionId) => {
    const { value, checked } = event.target;

    setSelectedValues((prevValues) => {
      const currentValues = Array.isArray(prevValues[questionId])
        ? prevValues[questionId]
        : [];

      const updatedValues = checked
        ? [...currentValues, value]
        : currentValues?.filter((item) => item !== value);
      return {
        ...prevValues,
        [questionId]: updatedValues,
      };
    });

    if (value === "Other") {
      setTextAnswers((prevAnswers) => {
        const newAnswers = { ...prevAnswers };
        if (checked) {
          newAnswers[`other-${questionId}`] = "";
        } else {
          delete newAnswers[`other-${questionId}`];
        }
        return newAnswers;
      });
    }
  };

  const handleSingleSelect = (questionId, selectedOption) => {
    setSelectedValues((prevSelectedValues) => ({
      ...prevSelectedValues,
      [questionId]: selectedOption,
    }));

    if (selectedOption === "Other") {
      setTextAnswers((prevAnswers) => ({
        ...prevAnswers,
        [`other-${questionId}`]: "",
      }));
    }
  };

  const handleTextInputChange = (questionId, value) => {
    setTextAnswers((prevAnswers) => ({
      ...prevAnswers,
      [questionId]: value,
    }));
  };

  const handleDateInputChange = (questionId, value) => {
    setDateAnswers((prevAnswers) => ({
      ...prevAnswers,
      [questionId]: value,
    }));
  };
  const getAnswerList = () => {
    return materialityQuestions.map((question) => {
      const type = question?.question_type?.toLowerCase();
      const questionId = question?.id;
      const condition = question?.condition;

      // Initialize default values
      let answer = null;
      let answerValue = "";
      let otherAnswer = textAnswers?.[`other-${questionId}`] || "";
      let multiselectTextAnswers = undefined;
      // Handle "Corporate address" separately by fetching sub-question answers
      const subQuestionDict = {};
      if (question.question === "Corporate address") {
        const children = subQuestionDict[question.id] || [];

        const streetId = question.id;
        const unitId = children.find((c) => c.question === "Unit Number")?.id;
        const countryId = children.find(
          (c) => c.question === "Select Country"
        )?.id;
        const stateId = children.find((c) => c.question === "Select State")?.id;
        const postalId = children.find((c) => c.question === "Postal Code")?.id;

        answer = {
          street: textAnswers[streetId] || "",
          unit: textAnswers[unitId] || "",
          country: textAnswers[countryId] || "",
          state: textAnswers[stateId] || "",
          postalCode: textAnswers[postalId] || "",
        };
      }

      // Handle answer based on question type
      switch (type) {
        case "text":
        case "text field":
        case "short_text":
        case "drop_down":
          answer = textAnswers?.[questionId] || "";
          break;

        case "choose_from_option":
        case "choose from options":
          answer = selectedValues?.[questionId] || "";
          if (answer === "Other (please specify):") {
            answerValue = textAnswers?.[`other-${questionId}`] || "";
          }
          break;

        case "multiselect_check_box":
          let selectedOptions = selectedValues?.[questionId] || [];

          if (typeof selectedOptions === "string") {
            selectedOptions = selectedOptions.split(", ");
          }

          // Deduplicate!
          const uniqueOptions = [...new Set(selectedOptions)];

          answer = uniqueOptions;

          if (uniqueOptions.includes("Other (please specify):")) {
            answerValue = textAnswers?.[`other-${questionId}`] || "";
          }
          break;

        case "date":
          answer = dateAnswers?.[questionId]
            ? dayjs(dateAnswers[questionId]).format("DD/MM/YYYY")
            : "";
          break;

        case "table":
          switch (question.condition) {
            case "table_gri_1":
              answer = assetValueTable;
              break;
            case "table_gri_2":
              answer = marketReservedTable;
              break;
            case "table_gri_3":
              answer = supplyChainTable;
              break;
            case "table_gri_4":
              answer = downstreamTable;
              break;
            case "table_gri_5":
              answer = lawRegulationTable;
              break;
            case "table_gri_6":
              answer = materialTopicsTable;
              break;
            case "table_sasb_aerospace_&_defence_1":
              answer = corruptionTable;
              break;
            case "table_sasb_biotechnology_1":
              answer = initiativeTable;
              break;
            case "table_sasb_biotechnology_2":
              answer = drugTable;
              break;
            case "table_sasb_biotechnology_3":
              answer = turnoverTable;
              break;
            case "table_sasb_forestry_management_1":
              answer = forestLandTable;
              break;
            case "table_sasb_airlines_1":
              answer = strikesTable;
              break;
            case "table_sasb_iron_steel_1":
              answer = pollutantEmissionTable;
              break;
            case "table_sasb_iron_steel_2":
              answer = workforceTable;
              break;
            case "table_sasb_iron_steel_3":
              answer = steelProductionTable;
              break;
            case "table_sasb_chemicals_2":
              answer = workforceTable1;
              break;
            case "table_sasb_chemicals_1":
              answer = pollutantEmissionTable;
              break;
            case "table_sasb_chemicals_3":
              answer = processSafetyTable;
              break;
            case "table_sasb_advertising_&_marketing_1":
              answer = employeeCategoryTable;
              break;
            case "table_sasb_advertising_&_marketing_2":
              answer = employeeCategoryTable1;
              break;
            case "table_sasb_media_&_entertainment_1":
              answer = genderRepresentationTabel;
              break;
            case "table_sasb_media_&_entertainment_2":
              answer = groupRepresentationTabel;
              break;
            case "table_sasb_engineering_&_construction_service_1":
              answer = workforceTable;
              break;
            case "brsr_table_1":
              answer = employeeTable;
              break;
            case "brsr_table_2":
              answer = employeeTable2;
              break;
            case "brsr_table_3":
              answer = shareholdingTable;
              break;
            case "brsr_table_4":
              answer = grievanceTable;
              break;
            case "brsr_table_5":
              answer = materialTable;
              break;
            case "brsr_table_6":
              answer = disclosureData;
              break;
            case "brsr_table_7":
              answer = reviewData;
              break;
            case "brsr_table_8":
              answer = frequencyData;
              break;
            case "brsr_table_9":
              answer = reasonData;
              break;
            case "brsr_table_10":
              answer = trainingData;
              break;
            case "brsr_table_11":
              answer = legalData;
              break;
            case "brsr_table_12":
              answer = caseStudies;
              break;
            case "brsr_table_13":
              answer = data;
              break;
            case "brsr_table_14":
              answer = conflictData;
              break;
            case "brsr_table_15":
              answer = payableData;
              break;
            case "brsr_table_16":
              answer = concentrationtableData;
              break;
            case "brsr_table_17":
              answer = awarenessData;
              break;
            case "brsr_table_18":
              answer = improvementData;
              break;
            case "brsr_table_19":
              answer = improvementData1;
              break;
            case "brsr_table_20":
              answer = productrows;
              break;
            case "brsr_table_21":
              answer = recycledData;
              break;
            case "brsr_table_22":
              answer = wastedata;
              break;
            case "brsr_table_23":
              answer = packagedata;
              break;
            case "brsr_table_24":
              answer = benifitTable;
              break;
            case "brsr_table_25":
              answer = workersbenefitTable;
              break;
            case "brsr_table_26":
              answer = wellbeingdata;
              break;
            case "brsr_table_27":
              answer = statutorydata;
              break;
            case "brsr_table_28":
              answer = returndata;
              break;
            case "brsr_table_29":
              answer = reclaimedproductdata;
              break;
            case "brsr_table_30":
              answer = uniondata;
              break;
            case "brsr_table_31":
              answer = trainingmeasuresdata;
              break;
            case "brsr_table_32":
              answer = fydata;
              break;
            case "brsr_table_33":
              answer = safetyData;
              break;
            case "brsr_table_34":
              answer = pfdata;
              break;
            case "brsr_table_35":
              answer = assessmentData;
              break;
            case "brsr_table_36":
              answer = rehabdata;
              break;
            case "brsr_table_37":
              answer = valuedata;
              break;
            case "brsr_table_38":
              answer = stakeholderdata;
              break;
            case "brsr_table_39":
              answer = coveragedata;
              break;
            case "brsr_table_40":
              answer = wageData;
              break;
            case "brsr_table_41":
              answer = remunerationdata;
              break;
            case "brsr_table_42":
              answer = femaleWageData;
              break;
            case "brsr_table_43":
              answer = humanrightsdata;
              break;
            case "brsr_table_44":
              answer = poshData;
              break;
            case "brsr_table_45":
              answer = plantAssessmentData;
              break;
            case "brsr_table_46":
              answer = valueChainAssessmentData;
              break;
            case "brsr_table_47":
              answer = energyData;
              break;
            case "brsr_table_48":
              answer = waterData;
              break;
            case "brsr_table_49":
              answer = dischargeData;
              break;
            case "brsr_table_50":
              answer = airdata;
              break;
            case "brsr_table_51":
              answer = scopeData;
              break;
            case "brsr_table_52":
              answer = wasteManagementData;
              break;
            case "brsr_table_53":
              answer = envclearanceData;
              break;
            case "brsr_table_54":
              answer = eiaData;
              break;
            case "brsr_table_55":
              answer = nonComplianceData;
              break;
            case "brsr_table_56":
              answer = waterusagedata;
              break;
            case "brsr_table_57":
              answer = scope3Data;
              break;
            case "brsr_table_58":
              answer = initiativeData;
              break;
            case "brsr_table_59":
              answer = associations;
              break;
            case "brsr_table_60":
              answer = authorityData;
              break;
            case "brsr_table_61":
              answer = advocacyData;
              break;
            case "brsr_table_62":
              answer = siaData;
              break;
            case "brsr_table_63":
              answer = resettlementData;
              break;
            case "brsr_table_64":
              answer = sourcingData;
              break;
            case "brsr_table_65":
              answer = locationData;
              break;
            case "brsr_table_66":
              answer = socialImpactData;
              break;
            case "brsr_table_67":
              answer = aspirationalDistrictData;
              break;
            case "brsr_table_68":
              answer = traditionalKnowledgeData;
              break;
            case "brsr_table_69":
              answer = regulatoryCaseData;
              break;
            case "brsr_table_70":
              answer = csrBeneficiaryData;
              break;
            case "brsr_table_71":
              answer = turnoverData;
              break;
            case "brsr_table_72":
              answer = complaintData;
              break;
            case "brsr_table_73":
              answer = recallData;
              break;
            case "table_sasb_software_&_it_services_1":
              answer = workforceGenderTable;
              break;
            case "table_sasb_software_&_it_services_2":
              answer = diversityGroupTable;
              break;
            case "table_sasb_car_rental_&_leasing_1":
              answer = fleetTable;
              break;
            case "table_sasb_mortgage_finance_1":
              answer = mortgageTable;
              break;
            case "table_sasb_mortgage_finance_2":
              answer = resolutionTable;
              break;
            case "table_sasb_mortgage_finance_3":
              answer = borrowerTypeTable;
              break;
            case "table_sasb_solar_technology_&_project_developers":
              answer = delaysTable;
              break;
            case "ifrs_table_1":
              answer = riskOpportunityData;
              break;
            case "ifrs_table_2":
              answer = opportunityData;
              break;
            case "ifrs_table_3":
              answer = riskActionsProgressData;
              break;
            case "ifrs_table_4":
              answer = financialEffectsData;
              break;
            case "ifrs_table_5":
              answer = timeHorizonABCTableData;
              break;
            case "ifrs_table_6":
              answer = sustainabilityRiskAssessData;
              break;
            case "ifrs_table_7":
              answer = targetsDisclosureData;
              break;
            case "ifrs_table_8":
              answer = climateRiskMatrixData;
              break;
            case "ifrs_table_9":
              answer = anticipatedFinancialEffectsData;
              break;
            case "ifrs_table_10":
              answer = elementsConsideredEC;
              break;
            case "ifrs_table_11":
              answer = emissionsMeasurementTE;
              break;
            case "ifrs_table_12":
              answer = emissionsByTypeERO;
              break;
            case "ifrs_table_13":
              answer = targetDetailsTDT;
              break;

            default:
              answer =
                questionId === "677770f39e7e8a6081643369"
                  ? riskTable
                  : resilienceTable;
          }
          break;

        default:
          // Handle any unsupported question types
          answer = null;
      }

      if (
        question.question ==
        "Please specify the types of enforcement actions (fill numbers):"
      ) {
        multiselectTextAnswers = Object.keys(textAnswers)
          .filter((key) => key.includes(questionId))
          .reduce((obj, key) => {
            obj[key.split("-")[2]] = textAnswers[key];
            return obj;
          }, {});
      }

      return {
        question_id: questionId,
        question_name: question?.question,
        question_type: question?.question_type,
        answer,
        ...(answerValue && { answer_value: answerValue }),
        ...(otherAnswer && { other: otherAnswer }),
        ...(multiselectTextAnswers && multiselectTextAnswers),
        condition: condition,
      };
    });
  };

  // Normalize helpers
  const norm = (s = "") =>
    s
      .toLowerCase()
      .replace(/\s+/g, " ")
      .replace(/[:：]\s*$/, "")
      .trim();

  // Find the IFRS "capacity to adjust/adapt" question id (single-select + extra text)
  const capacityQId = React.useMemo(() => {
    const wantOpts = [
      "Financial resource availability & flexibility",
      "Redeploying, repurposing, upgrading, or decommissioning assets",
      "Investments in mitigation, adaptation, or resilience",
    ].map(norm);

    const q = (materialityQuestions || []).find(
      (q) =>
        norm(q.section) === "strategy" &&
        norm(q.sub_section) === "climate resilience" &&
        norm(q.question_type) === "choose_from_option" &&
        q.condition === "extra_text" &&
        Array.isArray(q.options) &&
        q.options.length === 3 &&
        q.options.map(norm).every((o) => wantOpts.includes(o))
    );

    return q?.id ?? null;
  }, [materialityQuestions]);

  // Find the IFRS "Where are risks/opportunities concentrated?" question id (multiselect + per-option text)
  const whereConcentratedQId = React.useMemo(() => {
    const mustHave = [
      "Geographical areas:",
      "Facilities:",
      "Types of assets:",
      "Others (specify):",
    ].map(norm);

    const q = (materialityQuestions || []).find(
      (q) =>
        norm(q.section) === "strategy" &&
        norm(q.question_type) === "multiselect_check_box" &&
        Array.isArray(q.options) &&
        mustHave.every((label) => q.options.map(norm).includes(label))
    );

    return q?.id ?? null;
  }, [materialityQuestions]);

  const optionTextKey = (qid, option) => `${qid}::${option}`;
  const startsWithOther = (s = "") => /^\s*others?\b/i.test(String(s).trim());

  function enhanceAnswerList(list) {
    return (list || []).map((entry) => {
      if (!entry || !entry.question_id) return entry;

      const qid = entry.question_id;
      const ans = entry.answer;
      let other;

      // --- WHERE CONCENTRATED: multiselect with per-option textboxes ---
      if (whereConcentratedQId && qid === whereConcentratedQId) {
        let picks = Array.isArray(ans) ? ans : [];
        if (!Array.isArray(picks) && typeof ans === "string") {
          picks = ans.split(/,\s*/).filter(Boolean);
        }

        const lines = picks.map((opt) => {
          const t = (textAnswers?.[optionTextKey(qid, opt)] ?? "").trim();
          const hasColon = /[:：]\s*$/.test(opt);
          if (t) {
            const sep = hasColon ? " " : ": ";
            return `${opt}${sep}${t}`;
          }

          return hasColon ? opt : `${opt}:`;
        });

        other = lines.join(" | ");
      }

      // --- Generic “Other (Please specify)” (single/multi) ---
      if (!other && Array.isArray(ans) && ans.some(startsWithOther)) {
        const v = textAnswers?.[`other-${qid}`];
        if (v) other = v;
      }

      // --- CAPACITY single-select + companion textbox ---
      if (!other && capacityQId && qid === capacityQId) {
        const sel = Array.isArray(ans) ? ans[0] : ans;
        if (sel) {
          const v =
            textAnswers?.[optionTextKey(qid, sel)] ?? textAnswers?.[qid];
          if (v) other = v;
        }
      }

      return other != null ? { ...entry, other } : entry;
    });
  }

  const requestData = {
    id: frameworkId,
    payload: {
      answer_list: enhanceAnswerList(getAnswerList()),

      country: country,
      assessment_year: year,
    },
  };

  const answerListData = requestData.payload.answer_list;

  const onSave = () => {
    if (readOnlyMode) return;
    dispatch(saveStandardsQuestionnaire(requestData));
  };

  const onSubmit = () => {
    if (readOnlyMode) return;
    dispatch(submitStandardsQuestionnaire(requestData));
  };
  return (
    <DashboardLayout>
      <Grid container>
        <Box className={classes.buttonContainer}>
          <div className={classes.backContainer} onClick={navigateBack}>
            <ArrowBackIosIcon />
            Back
          </div>
          <div>
            <CeroButton
              buttonText="History"
              className={classes.buttonPrimary}
              onClick={() => navigate("list")}
            />
          </div>
        </Box>
        <Typography mt={4} mb={4}>
          Choose Country & Assessment Year
        </Typography>
        <Grid container xs={12}>
          <Grid item xs={3} className={classes.filterContainer}>
            <CeroDropdown
              id="country"
              label="Country"
              fullWidth
              options={countryOptions}
              onChange={handleCountryChange}
              selectedValue={country}
            />
          </Grid>
          <Grid item xs={3} className={classes.filterContainer}>
            <CeroDropdown
              id="year"
              label="Assessment Year"
              fullWidth
              options={transformedYearList}
              onChange={handleYearChange}
              selectedValue={year}
            />
          </Grid>
          {frameworkName && frameworkId && (
            <Typography
              style={{
                alignContent: "center",
                fontWeight: 430,
                marginLeft: 10,
              }}
            >
              {frameworkName} Framework
            </Typography>
          )}
        </Grid>
        {frameworkName && frameworkId && (
          <Paper className={classes.questions}>
            <Grid margin={5}>
              {frameworkName && frameworkId && (
                <Grid>
                  <Typography
                    style={{
                      alignContent: "center",
                      fontWeight: 430,
                      marginLeft: 10,
                    }}
                  >
                    {/* {frameworkName} Standards Questionnaire */}
                    {frameworkName === "GRI"
                      ? "GLOBAL REPORTING INITIATIVE QUESTIONNAIRE"
                      : frameworkName === "BRSR"
                      ? `BUSINESS RESPONSIBILITY & SUSTAINABILITY REPORTING QUESTIONNAIRE`
                      : `${frameworkName} Standards Questionnaire`}
                  </Typography>
                </Grid>
              )}
              {frameworkId &&
              materialityQuestions.length > 0 &&
              ["tcfd"].includes(frameworkName?.toLowerCase()) ? (
                <Grid mb={20}>
                  <div className={classes.questionList}>
                    {(() => {
                      const questionDict = {};

                      materialityQuestions.forEach((question) => {
                        const main = question.main_section || "General";
                        const section = question.section || "Section";
                        const sub = question.sub_section || "General";

                        if (!questionDict[main]) questionDict[main] = {};
                        if (!questionDict[main][section])
                          questionDict[main][section] = {};
                        if (!questionDict[main][section][sub])
                          questionDict[main][section][sub] = [];

                        questionDict[main][section][sub].push(question);
                      });

                      let globalCounter = 1;

                      return Object.entries(questionDict).map(
                        ([main, sections], mainIdx) => (
                          <React.Fragment key={mainIdx}>
                            <div className={classes.scoreDiv}>
                              <Typography
                                fontSize={22}
                                fontWeight={600}
                                variant="h2"
                                paddingTop="10px"
                              >
                                {`${toRoman(mainIdx + 1)}. ${main} `}
                              </Typography>
                            </div>

                            {Object.entries(sections).map(
                              ([section, subSections], sectionIdx) => (
                                <div
                                  key={sectionIdx}
                                  style={{ marginBottom: "20px" }}
                                >
                                  <Typography
                                    fontSize={20}
                                    fontWeight={550}
                                    variant="h3"
                                    paddingTop="10px"
                                  >
                                    {`${sectionIdx + 1}. ${section}`}
                                  </Typography>

                                  {Object.entries(subSections).map(
                                    (
                                      [subSection, questions],
                                      subSectionIdx
                                    ) => (
                                      <div
                                        key={subSectionIdx}
                                        style={{ marginTop: "12px" }}
                                      >
                                        {questions.map((question, qIdx) => {
                                          const isIfYes = question?.question
                                            ?.toLowerCase()
                                            ?.includes("if yes");
                                          const prevQ = questions[qIdx - 1];
                                          const isYesSelected =
                                            selectedValues?.[prevQ?.id] ===
                                            "Yes";

                                          if (isIfYes && !isYesSelected)
                                            return null;

                                          const questionNumber = isIfYes
                                            ? null
                                            : globalCounter++;

                                          return (
                                            <Grid
                                              key={question.id}
                                              className={
                                                question.question_type ===
                                                  "text" ||
                                                question.question_type ===
                                                  "Text Field"
                                                  ? classes.inputGrid
                                                  : classes.gridItem
                                              }
                                            >
                                              {isIfYes ? (
                                                <Typography
                                                  fontSize={17}
                                                  fontWeight={400}
                                                  variant="body1"
                                                  paddingLeft={6}
                                                  paddingBottom={1}
                                                  // style={{ fontStyle: "italic" }}
                                                >
                                                  {question.question}
                                                </Typography>
                                              ) : (
                                                <Typography
                                                  fontSize={18}
                                                  fontWeight={500}
                                                  variant="h3"
                                                  paddingLeft={4}
                                                  paddingBottom={2}
                                                >
                                                  {questionNumber}.{" "}
                                                  {question.question}
                                                </Typography>
                                              )}

                                              <Box
                                                display="flex"
                                                paddingLeft={6}
                                                flexWrap="wrap"
                                              >
                                                {(question.question_type ===
                                                  "choose_from_option" ||
                                                  question.question_type ===
                                                    "Choose from Options") &&
                                                  question.options.map(
                                                    (option, idx) => (
                                                      <React.Fragment key={idx}>
                                                        <FormControlLabel
                                                          control={
                                                            <Checkbox
                                                              className={
                                                                classes.selectCheckbox
                                                              }
                                                              checked={
                                                                selectedValues?.[
                                                                  question.id
                                                                ] === option
                                                              }
                                                              onChange={() =>
                                                                handleSingleSelect(
                                                                  question.id,
                                                                  option
                                                                )
                                                              }
                                                              value={option}
                                                              sx={{
                                                                color:
                                                                  "#5A404E !important",
                                                                "&.Mui-checked":
                                                                  {
                                                                    color:
                                                                      "#8C2148 !important",
                                                                  },
                                                              }}
                                                            />
                                                          }
                                                          label={option}
                                                        />
                                                        {selectedValues?.[
                                                          question.id
                                                        ] ===
                                                          "Other (Please specify)" &&
                                                          option ===
                                                            "Other (Please specify)" && (
                                                            <TextField
                                                              variant="outlined"
                                                              placeholder="Please type your Answer"
                                                              multiline
                                                              rows={3}
                                                              value={
                                                                textAnswers[
                                                                  `other-${question.id}`
                                                                ] || ""
                                                              }
                                                              onChange={(e) =>
                                                                handleTextInputChange(
                                                                  `other-${question.id}`,
                                                                  e.target.value
                                                                )
                                                              }
                                                              className={
                                                                classes.input
                                                              }
                                                            />
                                                          )}
                                                      </React.Fragment>
                                                    )
                                                  )}

                                                {question.question_type ===
                                                  "multiselect_check_box" &&
                                                  question.options.map(
                                                    (option, idx) => (
                                                      <React.Fragment key={idx}>
                                                        <FormControlLabel
                                                          control={
                                                            <Checkbox
                                                              className={
                                                                classes.selectCheckbox
                                                              }
                                                              checked={
                                                                selectedValues?.[
                                                                  question.id
                                                                ]?.includes(
                                                                  option
                                                                ) || false
                                                              }
                                                              onChange={(e) =>
                                                                handleCheckboxChange(
                                                                  e,
                                                                  question.id
                                                                )
                                                              }
                                                              value={option}
                                                              sx={{
                                                                color:
                                                                  "#5A404E !important",
                                                                "&.Mui-checked":
                                                                  {
                                                                    color:
                                                                      "#8C2148 !important",
                                                                  },
                                                              }}
                                                            />
                                                          }
                                                          label={option}
                                                        />
                                                        {selectedValues?.[
                                                          question.id
                                                        ]?.includes(
                                                          "Other (Please specify)"
                                                        ) &&
                                                          option ===
                                                            "Other (Please specify)" && (
                                                            <Box
                                                              width={"100%"}
                                                              mt={2}
                                                            >
                                                              <TextField
                                                                variant="outlined"
                                                                placeholder="Please type your Answer"
                                                                multiline
                                                                rows={3}
                                                                value={
                                                                  textAnswers[
                                                                    `other-${question.id}`
                                                                  ] || ""
                                                                }
                                                                onChange={(e) =>
                                                                  handleTextInputChange(
                                                                    `other-${question.id}`,
                                                                    e.target
                                                                      .value
                                                                  )
                                                                }
                                                                className={
                                                                  classes.input
                                                                }
                                                              />
                                                            </Box>
                                                          )}
                                                      </React.Fragment>
                                                    )
                                                  )}

                                                {(question.question_type ===
                                                  "text" ||
                                                  question.question_type ===
                                                    "Text Field" ||
                                                  question.question_type ===
                                                    "short_text") && (
                                                  <Box
                                                    minHeight={40}
                                                    width={"100%"}
                                                  >
                                                    <CeroInput
                                                      className={
                                                        classes.textAreaContainer
                                                      }
                                                      rows={2}
                                                      placeholder="Please type your Answer"
                                                      id={`answer-${question.id}`}
                                                      name={`answer-${question.id}`}
                                                      label="Please type your Answer"
                                                      multiline
                                                      value={
                                                        textAnswers[
                                                          question.id
                                                        ] || ""
                                                      }
                                                      onChange={(e) =>
                                                        handleTextInputChange(
                                                          question.id,
                                                          e.target.value
                                                        )
                                                      }
                                                    />
                                                  </Box>
                                                )}

                                                {question.question_type ===
                                                  "table" && (
                                                  <Grid width={"100%"}>
                                                    {question.instructions?.map(
                                                      (inst, i) => (
                                                        <Typography
                                                          paddingLeft={6}
                                                          paddingY={1}
                                                          key={i}
                                                        >
                                                          {i + 1}. {inst}
                                                        </Typography>
                                                      )
                                                    )}
                                                    {question.id ===
                                                    "677770f39e7e8a6081643369" ? (
                                                      <RiskTable
                                                        riskTable={riskTable}
                                                        setRiskTable={
                                                          setRiskTable
                                                        }
                                                      />
                                                    ) : (
                                                      <ResilienceTable
                                                        resilienceTable={
                                                          resilienceTable
                                                        }
                                                        setResilienceTable={
                                                          setResilienceTable
                                                        }
                                                      />
                                                    )}
                                                  </Grid>
                                                )}
                                              </Box>
                                            </Grid>
                                          );
                                        })}
                                      </div>
                                    )
                                  )}
                                </div>
                              )
                            )}
                          </React.Fragment>
                        )
                      );
                    })()}
                  </div>
                </Grid>
              ) : frameworkName?.toLowerCase() === "sasb" ? (
                <SasbQuestionnaire
                  handleCheckboxChange={handleCheckboxChange}
                  handleSingleSelect={handleSingleSelect}
                  handleTextInputChange={handleTextInputChange}
                  textAnswers={textAnswers}
                  selectedValues={selectedValues}
                  strikesTable={strikesTable}
                  setStrikesTable={setStrikesTable}
                  forestLandTable={forestLandTable}
                  setForestLandTable={setForestLandTable}
                  employeeCategoryTable={employeeCategoryTable}
                  setEmployeeCategoryTable={setEmployeeCategoryTable}
                  employeeCategoryTable1={employeeCategoryTable1}
                  setEmployeeCategoryTable1={setEmployeeCategoryTable1}
                  pollutantEmissionTable={pollutantEmissionTable}
                  setPollutantEmissionTable={setPollutantEmissionTable}
                  steelProductionTable={steelProductionTable}
                  setSteelProductionTable={setSteelProductionTable}
                  initiativeTable={initiativeTable}
                  setInitiativeTable={setInitiativeTable}
                  drugTable={drugTable}
                  setDrugTable={setDrugTable}
                  turnoverTable={turnoverTable}
                  setTurnoverTable={setTurnoverTable}
                  corruptionTable={corruptionTable}
                  setCorruptionTable={setCorruptionTable}
                  workforceTable={workforceTable}
                  setWorkforceTable={setWorkforceTable}
                  workforceTable1={workforceTable1}
                  setWorkforceTable1={setWorkforceTable1}
                  processSafetyTable={processSafetyTable}
                  setProcessSafetyTable={setProcessSafetyTable}
                  handleDateInputChange={handleDateInputChange}
                  genderRepresentationTabel={genderRepresentationTabel}
                  setGenderRepresentationTabel={setGenderRepresentationTabel}
                  groupRepresentationTabel={groupRepresentationTabel}
                  setGroupRepresentationTabel={setGroupRepresentationTabel}
                  workforceGenderTable={workforceGenderTable}
                  setWorkforceGenderTable={setWorkforceGenderTable}
                  diversityGroupTable={diversityGroupTable}
                  setDiversityGroupTable={setDiversityGroupTable}
                  fleetTable={fleetTable}
                  setFleetTable={setFleetTable}
                  mortgageTable={mortgageTable}
                  setMortgageTable={setMortgageTable}
                  resolutionTable={resolutionTable}
                  setResolutionTable={setResolutionTable}
                  borrowerTypeTable={borrowerTypeTable}
                  setBorrowerTypeTable={setBorrowerTypeTable}
                  delaysTable={delaysTable}
                  setDelaysTable={setDelaysTable}
                  dateAnswers={dateAnswers}
                />
              ) : frameworkName?.toLowerCase() === "gri" ? (
                <GriQuestionnaire
                  handleCheckboxChange={handleCheckboxChange}
                  handleSingleSelect={handleSingleSelect}
                  handleTextInputChange={handleTextInputChange}
                  textAnswers={textAnswers}
                  selectedValues={selectedValues}
                  assetValueTable={assetValueTable}
                  setAssetValueTable={setAssetValueTable}
                  marketReservedTable={marketReservedTable}
                  setmarketReservedTable={setmarketReservedTable}
                  supplyChainTable={supplyChainTable}
                  setSupplyChainTable={setSupplyChainTable}
                  downstreamTable={downstreamTable}
                  setDownstreamTable={setDownstreamTable}
                  lawRegulationTable={lawRegulationTable}
                  setLawRegulationTable={setLawRegulationTable}
                  materialTopicsTable={materialTopicsTable}
                  setMaterialTopicsTable={setMaterialTopicsTable}
                  handleDateInputChange={handleDateInputChange}
                  dateAnswers={dateAnswers}
                />
              ) : frameworkName?.toLowerCase() === "csrd" ? (
                <CsrdQuestionnaire
                  handleCheckboxChange={handleCheckboxChange}
                  handleSingleSelect={handleSingleSelect}
                  handleTextInputChange={handleTextInputChange}
                  textAnswers={textAnswers}
                  selectedValues={selectedValues}
                  assetValueTable={assetValueTable}
                  setAssetValueTable={setAssetValueTable}
                  oversightFormTable={oversightFormTable}
                  setOversightFormTable={setoversightFormTable}
                  dueDiligenceData={dueDiligenceData}
                  setDueDiligenceData={setDueDiligenceData}
                  policyData={policyData}
                  setPolicyData={setPolicyData}
                  resourceData={resourceData}
                  setResourceData={setResourceData}
                  targetsData={targetsData}
                  setTargetsData={setTargetsData}
                  handleDateInputChange={handleDateInputChange}
                  dateAnswers={dateAnswers}
                  assessmentYear={year} // ✅ pass here**
                  selectedCountry={country}
                />
              ) : frameworkName?.toLowerCase() === "ifrs" ? (
                <IfrsQuestionnaire
                  handleCheckboxChange={handleCheckboxChange}
                  handleSingleSelect={handleSingleSelect}
                  handleTextInputChange={handleTextInputChange}
                  textAnswers={textAnswers}
                  selectedValues={selectedValues}
                  riskOpportunityData={riskOpportunityData}
                  setRiskOpportunityData={setRiskOpportunityData}
                  opportunityData={opportunityData}
                  setOpportunityData={setOpportunityData}
                  riskActionsProgressData={riskActionsProgressData}
                  setRiskActionsProgressData={setRiskActionsProgressData}
                  financialEffectsData={financialEffectsData}
                  setFinancialEffectsData={setFinancialEffectsData}
                  timeHorizonABCTableData={timeHorizonABCTableData}
                  setTimeHorizonABCTableData={setTimeHorizonABCTableData}
                  sustainabilityRiskAssessData={sustainabilityRiskAssessData}
                  setSustainabilityRiskAssessData={
                    setSustainabilityRiskAssessData
                  }
                  targetsDisclosureData={targetsDisclosureData}
                  setTargetsDisclosureData={setTargetsDisclosureData}
                  climateRiskMatrixData={climateRiskMatrixData}
                  setClimateRiskMatrixData={setClimateRiskMatrixData}
                  anticipatedFinancialEffectsData={
                    anticipatedFinancialEffectsData
                  }
                  setAnticipatedFinancialEffectsData={
                    setAnticipatedFinancialEffectsData
                  }
                  elementsConsideredEC={elementsConsideredEC}
                  setElementsConsideredEC={setElementsConsideredEC}
                  emissionsMeasurementTE={emissionsMeasurementTE}
                  setEmissionsMeasurementTE={setEmissionsMeasurementTE}
                  emissionsByTypeERO={emissionsByTypeERO}
                  setEmissionsByTypeERO={setEmissionsByTypeERO}
                  targetDetailsTDT={targetDetailsTDT}
                  setTargetDetailsTDT={setTargetDetailsTDT}
                  handleDateInputChange={handleDateInputChange}
                  setShowSaveSubmit={setShowSaveSubmit}
                  dateAnswers={dateAnswers}
                />
              ) : (
                <BrsrQuestionnaire
                  handleCheckboxChange={handleCheckboxChange}
                  handleSingleSelect={handleSingleSelect}
                  handleTextInputChange={handleTextInputChange}
                  textAnswers={textAnswers}
                  selectedValues={selectedValues}
                  employeeTable={employeeTable}
                  setEmployeeTable={setEmployeeTable}
                  employeeTable2={employeeTable2}
                  setEmployeeTable2={setEmployeeTable2}
                  shareholdingTable={shareholdingTable}
                  setShareholdingTable={setShareholdingTable}
                  grievanceTable={grievanceTable}
                  setGrievanceTable={setGrievanceTable}
                  materialTable={materialTable}
                  setMaterialTable={setMaterialTable}
                  disclosureData={disclosureData}
                  setDisclosureData={setDisclosureData}
                  reviewData={reviewData}
                  setReviewData={setReviewData}
                  frequencyData={frequencyData}
                  setFrequencyData={setFrequencyData}
                  reasonData={reasonData}
                  setReasonData={setReasonData}
                  trainingData={trainingData}
                  setTrainingData={setTrainingData}
                  legalData={legalData}
                  setLegalData={setLegalData}
                  caseStudies={caseStudies}
                  setCaseStudies={setCaseStudies}
                  data={data}
                  setData={setData}
                  conflictData={conflictData}
                  setConflictData={setConflictData}
                  payableData={payableData}
                  setPayableData={setPayableData}
                  concentrationtableData={concentrationtableData}
                  setConcentrationTableData={setConcentrationTableData}
                  awarenessData={awarenessData}
                  setAwarenessData={setAwarenessData}
                  improvementData={improvementData}
                  setImprovementData={setImprovementData}
                  improvementData1={improvementData1}
                  setImprovementData1={setImprovementData1}
                  productrows={productrows}
                  setProductRows={setProductRows}
                  recycledData={recycledData}
                  setRecycledData={setRecycledData}
                  wastedata={wastedata}
                  setWasteData={setWasteData}
                  packagedata={packagedata}
                  setPackageData={setPackageData}
                  benifitTable={benifitTable}
                  setBenifitTable={setBenifitTable}
                  workersbenefitTable={workersbenefitTable}
                  setWorkersBenefitTable={setWorkersBenefitTable}
                  wellbeingdata={wellbeingdata}
                  setWellbeingData={setWellbeingData}
                  statutorydata={statutorydata}
                  setStatutoryData={setStatutoryData}
                  returndata={returndata}
                  setReturnData={setReturnData}
                  reclaimedproductdata={reclaimedproductdata}
                  setReclaimedProductData={setReclaimedProductData}
                  uniondata={uniondata}
                  setUnionData={setUnionData}
                  trainingmeasuresdata={trainingmeasuresdata}
                  setTrainingMeasuresData={setTrainingMeasuresData}
                  fydata={fydata}
                  setFyData={setFyData}
                  safetyData={safetyData}
                  setSafetyData={setSafetyData}
                  pfdata={pfdata}
                  setPfData={setPfData}
                  assessmentData={assessmentData}
                  setAssessmentData={setAssessmentData}
                  rehabdata={rehabdata}
                  setRehabData={setRehabData}
                  valuedata={valuedata}
                  setValueData={setValueData}
                  stakeholderdata={stakeholderdata}
                  setStakeholderData={setStakeholderData}
                  coveragedata={coveragedata}
                  setCoverageData={setCoverageData}
                  wageData={wageData}
                  setWageData={setWageData}
                  remunerationdata={remunerationdata}
                  setRemunerationData={setRemunerationData}
                  femaleWageData={femaleWageData}
                  setFemaleWageData={setFemaleWageData}
                  humanrightsdata={humanrightsdata}
                  setHumanrightsData={setHumanrightsData}
                  poshData={poshData}
                  setPoshData={setPoshData}
                  plantAssessmentData={plantAssessmentData}
                  setPlantAssessmentData={setPlantAssessmentData}
                  valueChainAssessmentData={valueChainAssessmentData}
                  setValueChainAssessmentData={setValueChainAssessmentData}
                  energyData={energyData}
                  setEnergyData={setEnergyData}
                  waterData={waterData}
                  setWaterData={setWaterData}
                  dischargeData={dischargeData}
                  setDischargeData={setDischargeData}
                  airdata={airdata}
                  setAirData={setAirData}
                  scopeData={scopeData}
                  setScopeData={setScopeData}
                  wasteManagementData={wasteManagementData}
                  setWasteManagementData={setWasteManagementData}
                  envclearanceData={envclearanceData}
                  setEnvClearanceData={setEnvClearanceData}
                  eiaData={eiaData}
                  setEiaData={setEiaData}
                  nonComplianceData={nonComplianceData}
                  setNonComplianceData={setNonComplianceData}
                  waterusagedata={waterusagedata}
                  setWaterUsageData={setWaterUsageData}
                  scope3Data={scope3Data}
                  setScope3Data={setScope3Data}
                  initiativeData={initiativeData}
                  setInitiativeData={setInitiativeData}
                  associations={associations}
                  setAssociations={setAssociations}
                  authorityData={authorityData}
                  setAuthorityData={setAuthorityData}
                  advocacyData={advocacyData}
                  setAdvocacyData={setAdvocacyData}
                  siaData={siaData}
                  setSiaData={setSiaData}
                  resettlementData={resettlementData}
                  setResettlementData={setResettlementData}
                  sourcingData={sourcingData}
                  setSourcingData={setSourcingData}
                  locationData={locationData}
                  setLocationData={setLocationData}
                  socialImpactData={socialImpactData}
                  setSocialImpactData={setSocialImpactData}
                  aspirationalDistrictData={aspirationalDistrictData}
                  setAspirationalDistrictData={setAspirationalDistrictData}
                  traditionalKnowledgeData={traditionalKnowledgeData}
                  setTraditionalKnowledgeData={setTraditionalKnowledgeData}
                  regulatoryCaseData={regulatoryCaseData}
                  setRegulatoryCaseData={setRegulatoryCaseData}
                  csrBeneficiaryData={csrBeneficiaryData}
                  setCsrBeneficiaryData={setCsrBeneficiaryData}
                  turnoverData={turnoverData}
                  setTurnoverData={setTurnoverData}
                  complaintData={complaintData}
                  setComplaintData={setComplaintData}
                  recallData={recallData}
                  setRecallData={setRecallData}
                  handleDateInputChange={handleDateInputChange}
                  dateAnswers={dateAnswers}
                />
              )}
            </Grid>
{isSubmitAndSaveAllowed &&
  ((frameworkName?.toLowerCase() !== "ifrs") || showSaveSubmit === true) && (
    <Grid xs={12} className={classes.bottomContainer}>
      <CeroButton
        buttonText="Save"
        className={classes.savebutton}
        variant="outlined"
        onClick={onSave}
        disabled={readOnlyMode || !frameworkId}
      />
      <CeroButton
        buttonText="Submit"
        className={classes.submitbutton}
        onClick={onSubmit}
        disabled={
          readOnlyMode ||
          !answerListData ||
          answerListData.length === 0
        }
      />
    </Grid>
)}
          </Paper>
        )}
      </Grid>
    </DashboardLayout>
  );
}

export default StandardsQuestionnaire;
